import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import socketManager from '../lib/socket';
import { useAuthStore } from '../stores/authStore';
import {
  Lock,
  Crown,
  Loader2,
  Play,
  Pause,
  Save,
  RefreshCw,
  RotateCcw,
  AlertTriangle,
  CheckCircle2,
  Activity,
  TrendingUp,
  TrendingDown,
  Clock
} from 'lucide-react';

const MARKET_CATEGORY_OPTIONS = [
  { value: 'all', label: 'All markets' },
  { value: 'crypto', label: 'Crypto only' },
  { value: 'sports', label: 'Sports only' },
  { value: 'politics', label: 'Politics' },
  { value: 'elections', label: 'Elections' },
  { value: 'trump', label: 'Trump' },
  { value: 'world', label: 'World' },
  { value: 'science', label: 'Science' },
  { value: 'economy', label: 'Economy' },
  { value: 'tech', label: 'Tech' },
  { value: 'popculture', label: 'Pop Culture' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'business', label: 'Business' },
  { value: 'weather', label: 'Weather' },
  { value: 'legal', label: 'Legal' },
  { value: 'congress', label: 'Congress' },
  { value: 'supremecourt', label: 'Supreme Court' },
  { value: 'awards', label: 'Awards' },
  { value: 'ai', label: 'AI' },
  { value: 'spacex', label: 'SpaceX' },
  { value: 'elon', label: 'Elon Musk' },
  { value: 'ukraine', label: 'Ukraine' },
  { value: 'china', label: 'China' },
  { value: 'geopolitics', label: 'Geopolitics' }
];

const MARKET_CATEGORY_LABELS = MARKET_CATEGORY_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

function NumberField({ label, value, onChange, min, max, step, hint }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input"
        min={min}
        max={max}
        step={step}
      />
      {hint ? <p className="mt-1 text-xs text-dark-500">{hint}</p> : null}
    </div>
  );
}

function SelectField({ label, value, onChange, options, hint }) {
  return (
    <div>
      <label className="label">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="input">
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {hint ? <p className="mt-1 text-xs text-dark-500">{hint}</p> : null}
    </div>
  );
}

export default function WhaleVault() {
  const { user } = useAuthStore();
  const [tier, setTier] = useState(null);
  const [isTierLoading, setIsTierLoading] = useState(true);

  const [config, setConfig] = useState(null);
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState('');
  
  // Activity log state
  const [activity, setActivity] = useState([]);
  const [activityStats, setActivityStats] = useState(null);
  const [isActivityLoading, setIsActivityLoading] = useState(false);
  
  // Sentinel real-time activity - persist to localStorage for 1 hour
  const [sentinelActivity, setSentinelActivity] = useState(() => {
    try {
      const stored = localStorage.getItem('sentinelActivityLog');
      if (stored) {
        const { data, timestamp } = JSON.parse(stored);
        // Clear if older than 1 hour
        if (Date.now() - timestamp < 60 * 60 * 1000) {
          return data || [];
        }
      }
    } catch (e) {}
    return [];
  });
  
  // Sentinel PNL stats
  const [sentinelStats, setSentinelStats] = useState(null);
  const [isSentinelStatsLoading, setIsSentinelStatsLoading] = useState(false);
  const [isSentinelStatsResetting, setIsSentinelStatsResetting] = useState(false);

  const [marketSearchQuery, setMarketSearchQuery] = useState('');
  const [marketSearchResults, setMarketSearchResults] = useState([]);
  const [isMarketSearchLoading, setIsMarketSearchLoading] = useState(false);
  const [marketSearchError, setMarketSearchError] = useState('');

  const isWhale = (tier || 'free') === 'whale';
  const isStatusLoading = isConfigLoading || !config;

  const universeSummary = useMemo(() => {
    const u = config?.universe || {};
    const wlCount = Array.isArray(u.marketWhitelist) ? u.marketWhitelist.length : 0;
    if (wlCount > 0) return `Selected (${wlCount})`;

    const cat = (u.category || 'all').toString().toLowerCase();
    const base = MARKET_CATEGORY_LABELS[cat] || (cat === 'crypto' ? 'Crypto' : cat === 'sports' ? 'Sports' : 'All');

    if (cat !== 'all') return base;

    const flags = [];
    if (u.includeCrypto === false) flags.push('no crypto');
    if (u.includeSports === false) flags.push('no sports');
    return flags.length > 0 ? `${base} (${flags.join(', ')})` : base;
  }, [config]);

  useEffect(() => {
    let canceled = false;

    const fetchTier = async () => {
      setIsTierLoading(true);
      try {
        const res = await api.get('/payments/subscription');
        if (!canceled) {
          setTier(res.data?.effectiveTier || res.data?.tier || 'free');
        }
      } catch (e) {
        if (!canceled) {
          setTier('free');
        }
      } finally {
        if (!canceled) {
          setIsTierLoading(false);
        }
      }
    };

    fetchTier();
    return () => {
      canceled = true;
    };
  }, []);

  const fetchConfig = async () => {
    setError('');
    setMessage(null);
    setIsConfigLoading(true);
    try {
      const res = await api.get('/whale/config');
      setConfig(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to fetch Whale Mode config');
    } finally {
      setIsConfigLoading(false);
    }
  };

  const fetchActivity = async () => {
    setIsActivityLoading(true);
    try {
      const res = await api.get('/whale/activity?limit=20');
      setActivity(res.data.activity || []);
      setActivityStats(res.data.stats);
    } catch (e) {
      console.error('Failed to fetch activity:', e);
    } finally {
      setIsActivityLoading(false);
    }
  };
  
  const [isSyncing, setIsSyncing] = useState(false);
  const syncPositions = async () => {
    setIsSyncing(true);
    try {
      const res = await api.post('/whale/sync-positions');
      const { synced, skipped, errors } = res.data;
      if (synced > 0) {
        setMessage({ type: 'success', text: `Synced ${synced} position(s) from Polymarket with SL/TP applied` });
      } else if (skipped > 0) {
        setMessage({ type: 'info', text: `All ${skipped} positions already in sync` });
      } else {
        setMessage({ type: 'info', text: 'No new positions found to sync' });
      }
      fetchActivity();
    } catch (e) {
      setError('Failed to sync positions: ' + (e.response?.data?.error || e.message));
    } finally {
      setIsSyncing(false);
    }
  };

  const fetchSentinelStats = async () => {
    setIsSentinelStatsLoading(true);
    try {
      const res = await api.get('/positions/sentinel-stats');
      if (res.data?.success) {
        setSentinelStats(res.data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch sentinel stats:', err);
    } finally {
      setIsSentinelStatsLoading(false);
    }
  };

  const resetSentinelStats = async () => {
    const ok = window.confirm(
      'Reset Sentinel performance stats?\n\nThis resets win rate / PnL totals / history in this panel.\nIt will NOT close any open positions and it will NOT delete trades.\n\nContinue?'
    );
    if (!ok) return;

    setIsSentinelStatsResetting(true);
    try {
      const res = await api.post('/positions/sentinel-stats/reset');
      if (res.data?.success) {
        setMessage({ type: 'success', text: 'Sentinel stats reset.' });
      } else {
        setError('Failed to reset Sentinel stats');
      }
      await fetchSentinelStats();
    } catch (e) {
      setError('Failed to reset Sentinel stats: ' + (e.response?.data?.error || e.message));
    } finally {
      setIsSentinelStatsResetting(false);
    }
  };

  useEffect(() => {
    if (!isWhale) return;
    fetchConfig();
    fetchActivity();
    fetchSentinelStats();
    
    // Auto-refresh activity every 60 seconds (reduced from 30s to prevent flicker)
    const interval = setInterval(() => {
      fetchActivity();
      fetchSentinelStats();
    }, 60000);
    
    // Subscribe to real-time Sentinel activity
    socketManager.connect();
    
    // IMPORTANT: Join the whale room to receive sentinelActivity events
    if (user?._id) {
      socketManager.subscribeToWhale(user._id);
    }
    
    const handleSentinelActivity = (data) => {
      setSentinelActivity(prev => [data, ...prev].slice(0, 50));
    };
    
    socketManager.on('sentinelActivity', handleSentinelActivity);
    
    return () => {
      clearInterval(interval);
      socketManager.off('sentinelActivity', handleSentinelActivity);
      if (user?._id) {
        socketManager.unsubscribeFromWhale(user._id);
      }
    };
  }, [isWhale, user?._id]);

  // Persist sentinelActivity to localStorage whenever it changes
  useEffect(() => {
    if (sentinelActivity.length > 0) {
      try {
        localStorage.setItem('sentinelActivityLog', JSON.stringify({
          data: sentinelActivity,
          timestamp: Date.now()
        }));
      } catch (e) {}
    }
  }, [sentinelActivity]);

  // Clear sentinelActivity from localStorage when user clicks Clear
  const clearSentinelActivity = () => {
    setSentinelActivity([]);
    try {
      localStorage.removeItem('sentinelActivityLog');
    } catch (e) {}
  };

  const draft = useMemo(() => {
    if (!config) return null;

    const tf = config.timeframe || {};
    const risk = config.risk || {};
    const filters = config.filters || {};
    const signal = config.signal || {};
    const universe = config.universe || {};
    const ta = config.ta || {};

    const whitelist = Array.isArray(universe.marketWhitelist)
      ? universe.marketWhitelist
          .map((m) => ({
            conditionId: m?.conditionId,
            question: m?.question,
            slug: m?.slug
          }))
          .filter((m) => m.conditionId)
      : [];

    return {
      isActive: !!config.isActive,
      mode: config.mode || 'paper',
      signalMode: config.signalMode || 'microstructure',
      universe: {
        category: universe.category ?? 'all',
        includeSports: universe.includeSports ?? true,
        includeCrypto: universe.includeCrypto ?? true,
        marketWhitelist: whitelist
      },
      timeframe: {
        minHoursToExpiry: tf.minHoursToExpiry ?? 6,
        maxHoursToExpiry: tf.maxHoursToExpiry ?? 72
      },
      risk: {
        maxOpenPositions: risk.maxOpenPositions ?? 5,
        maxDailyTrades: risk.maxDailyTrades ?? 10,
        baseTradeAmount: risk.baseTradeAmount ?? 50,
        maxTradeAmount: risk.maxTradeAmount ?? 200,
        maxDailyLoss: risk.maxDailyLoss ?? 500,
        cooldownSeconds: risk.cooldownSeconds ?? 300,
        stopLoss: {
          enabled: risk.stopLoss?.enabled ?? false,
          percentage: risk.stopLoss?.percentage ?? 15,
          trailing: risk.stopLoss?.trailing ?? false
        },
        takeProfit: {
          enabled: risk.takeProfit?.enabled ?? false,
          percentage: risk.takeProfit?.percentage ?? 25
        },
        breakEvenStop: {
          enabled: risk.breakEvenStop?.enabled ?? false,
          triggerPercent: risk.breakEvenStop?.triggerPercent ?? 10
        }
      },
      filters: {
        minLiquidity: filters.minLiquidity ?? 5000,
        maxSpread: filters.maxSpread ?? 0.06,
        minVolume24h: filters.minVolume24h ?? 1000
      },
      signal: {
        threshold: signal.threshold ?? 0.55,
        lookbackPoints: signal.lookbackPoints ?? 24
      },
      bamMode: {
        enabled: config.bamMode?.enabled ?? false,
        overrideMaxPositions: config.bamMode?.overrideMaxPositions ?? true,
        overrideMaxTrades: config.bamMode?.overrideMaxTrades ?? true,
        minConfidence: config.bamMode?.minConfidence ?? 0.85,
        multiplier: config.bamMode?.multiplier ?? 1.5,
        minDataPoints: config.bamMode?.minDataPoints ?? 50
      },
      sentinelMode: {
        enabled: config.sentinelMode?.enabled ?? false,
        mode: config.sentinelMode?.mode ?? 'paper', // 'paper' or 'real'
        paperBankroll: config.sentinelMode?.paperBankroll ?? 10000,
        maxPortfolioRisk: config.sentinelMode?.maxPortfolioRisk ?? 15,
        maxSingleTradeRisk: config.sentinelMode?.maxSingleTradeRisk ?? 3,
        kellyFraction: config.sentinelMode?.kellyFraction ?? 0.25,
        minEdgeRequired: config.sentinelMode?.minEdgeRequired ?? 0,
        overrideRiskBlocks: config.sentinelMode?.overrideRiskBlocks ?? false,
        noStopLosses: config.sentinelMode?.noStopLosses ?? false,
        sellOnlyOver90Cents: config.sentinelMode?.sellOnlyOver90Cents ?? false,
        noStopLossesMarketFilter: config.sentinelMode?.noStopLossesMarketFilter ?? 'all',
        lossMitigationEnabled: config.sentinelMode?.lossMitigationEnabled ?? true,
        catastrophicLossFloor: config.sentinelMode?.catastrophicLossFloor ?? -70,
        halfSellAtLoss: config.sentinelMode?.halfSellAtLoss ?? -50,
        hedgeAfterHalfSell: config.sentinelMode?.hedgeAfterHalfSell ?? false,
        hedgeEnabled: config.sentinelMode?.hedgeEnabled ?? false,
        hedgeTriggerPercent: config.sentinelMode?.hedgeTriggerPercent ?? -25,
        confidenceBasedSizing: config.sentinelMode?.confidenceBasedSizing ?? true,
        marketTypeShares: {
          crypto: config.sentinelMode?.marketTypeShares?.crypto ?? 10,
          sports: config.sentinelMode?.marketTypeShares?.sports ?? 10,
          politics: config.sentinelMode?.marketTypeShares?.politics ?? 10,
          other: config.sentinelMode?.marketTypeShares?.other ?? 10
        },
        perMarketStopLossEnabled: config.sentinelMode?.perMarketStopLossEnabled ?? false,
        stopLossByMarketType: {
          crypto: config.sentinelMode?.stopLossByMarketType?.crypto ?? 80,
          sports: config.sentinelMode?.stopLossByMarketType?.sports ?? 30,
          politics: config.sentinelMode?.stopLossByMarketType?.politics ?? 40,
          other: config.sentinelMode?.stopLossByMarketType?.other ?? 40
        },
        tradeSizing: {
          mode: config.sentinelMode?.tradeSizing?.mode ?? 'kelly',
          usdAmount: config.sentinelMode?.tradeSizing?.usdAmount ?? 1,
          shareAmount: config.sentinelMode?.tradeSizing?.shareAmount ?? 10
        },
        orderType: config.sentinelMode?.orderType ?? 'GTC',
        dynamicTakeProfit: {
          enabled: config.sentinelMode?.dynamicTakeProfit?.enabled ?? true,
          basePercent: config.sentinelMode?.dynamicTakeProfit?.basePercent ?? 25,
          letItRide: config.sentinelMode?.dynamicTakeProfit?.letItRide ?? false,
          letItRideTrigger: config.sentinelMode?.dynamicTakeProfit?.letItRideTrigger ?? 15,
          letItRideTrailPercent: config.sentinelMode?.dynamicTakeProfit?.letItRideTrailPercent ?? 10,
          letItRideStopAtEntry: config.sentinelMode?.dynamicTakeProfit?.letItRideStopAtEntry ?? true
        },
        certaintyScalp: {
          enabled: config.sentinelMode?.certaintyScalp?.enabled ?? false,
          onlyMode: config.sentinelMode?.certaintyScalp?.onlyMode ?? false,
          overrideMaxPositions: config.sentinelMode?.certaintyScalp?.overrideMaxPositions ?? true,
          maxHoursToExpiry: config.sentinelMode?.certaintyScalp?.maxHoursToExpiry ?? 24,
          minFavoritePrice: config.sentinelMode?.certaintyScalp?.minFavoritePrice ?? 0.90,
          tradeAmountMultiplier: config.sentinelMode?.certaintyScalp?.tradeAmountMultiplier ?? 2.0,
          excludeCrypto: config.sentinelMode?.certaintyScalp?.excludeCrypto ?? true,
          stopLossPercent: config.sentinelMode?.certaintyScalp?.stopLossPercent ?? 20,
          disableTakeProfit: config.sentinelMode?.certaintyScalp?.disableTakeProfit ?? true
        }
      },
      discord: {
        enabled: config.discord?.enabled ?? false,
        webhookUrl: config.discord?.webhookUrl ?? ''
      },
      ta: {
        minConfluenceScore: ta.minConfluenceScore ?? 0.7,
        rsiPeriod: ta.rsiPeriod ?? 14,
        emaFast: ta.emaFast ?? 12,
        emaSlow: ta.emaSlow ?? 26,
        lookbackDays: ta.lookbackDays ?? 3
      },
      execution: {
        orderType: config.execution?.orderType ?? 'MARKET',
        slippage: config.execution?.slippage ?? 5
      }
    };
  }, [config]);

  const [form, setForm] = useState(null);
  const [formInitialized, setFormInitialized] = useState(false);

  useEffect(() => {
    // Only initialize form from draft ONCE on initial load
    // Don't reset form when config updates from server (would lose local changes)
    if (draft && !formInitialized) {
      setForm(draft);
      setFormInitialized(true);
    }
  }, [draft, formInitialized]);

  // ═══════════════════════════════════════════════════════════════════════════
  // SETTINGS HEALTH CHECK - Analyze config for dangerous combinations
  // ═══════════════════════════════════════════════════════════════════════════
  const settingsHealthCheck = useMemo(() => {
    const sm = form?.sentinelMode;
    if (!sm?.enabled) return { warnings: [], tips: [], activeFeatures: [] };

    const warnings = [];
    const tips = [];
    const activeFeatures = [];

    const noSL = sm.noStopLosses === true;
    const letItRide = sm.dynamicTakeProfit?.letItRide === true;
    const halfSellAt = sm.halfSellAtLoss ?? -50;
    const halfSellDisabled = halfSellAt === 0 || halfSellAt >= 0;
    const forceSellFloor = sm.catastrophicLossFloor ?? -70;
    const aggressive = sm.overrideRiskBlocks === true;
    const hedgeEnabled = sm.hedgeEnabled === true;
    const hedgeTrigger = sm.hedgeTriggerPercent ?? -25;
    const lirTrigger = sm.dynamicTakeProfit?.letItRideTrigger ?? 15;
    const lirTrail = sm.dynamicTakeProfit?.letItRideTrailPercent ?? 10;
    const hardTp = sm.dynamicTakeProfit?.basePercent ?? 25;

    // Typical entry range 70-80¢ → max gain 25-43%
    const typicalEntry = 0.77;
    const maxGainAtTypical = ((1 - typicalEntry) / typicalEntry) * 100; // ~30%

    // Active features list
    if (noSL) activeFeatures.push({ icon: '💎', label: 'No Stop Losses', status: 'ON', color: 'text-purple-400' });
    if (letItRide) activeFeatures.push({ icon: '🏄', label: `Let It Ride (trigger ${lirTrigger}%, trail ${lirTrail}%)`, status: 'ON', color: 'text-cyan-400' });
    if (!letItRide) activeFeatures.push({ icon: '💰', label: `Hard Take Profit at ${hardTp}%`, status: 'ON', color: 'text-green-400' });
    if (hedgeEnabled) activeFeatures.push({ icon: '🔄', label: `Hedge & Switch at ${Math.abs(hedgeTrigger)}% loss`, status: 'ON', color: 'text-cyan-400' });
    if (aggressive) activeFeatures.push({ icon: '⚡', label: 'Aggressive Mode', status: 'ON', color: 'text-yellow-400' });
    if (!halfSellDisabled) activeFeatures.push({ icon: '✂️', label: `Half-sell at ${Math.abs(halfSellAt)}% loss`, status: 'ON', color: 'text-orange-400' });
    if (halfSellDisabled && noSL) activeFeatures.push({ icon: '✂️', label: 'Half-sell DISABLED', status: 'OFF', color: 'text-red-400' });
    activeFeatures.push({ icon: '⏳', label: '2-min grace period on new positions', status: 'ON', color: 'text-slate-400' });

    // WARNINGS
    if (noSL && halfSellDisabled) {
      const effectiveLoss = Math.abs(forceSellFloor);
      warnings.push({
        severity: 'high',
        title: 'No loss protection until force-sell',
        message: `No Stop Losses ON + Half-sell DISABLED = every loser bleeds to -${effectiveLoss}% before exiting. At typical entries (77¢), max gain is only ~${maxGainAtTypical.toFixed(0)}%. Losers lose ${(effectiveLoss / maxGainAtTypical).toFixed(1)}x more than winners gain. You need ${Math.round(effectiveLoss / (effectiveLoss + maxGainAtTypical) * 100)}%+ win rate just to break even.`
      });
    }

    if (noSL && !halfSellDisabled && Math.abs(halfSellAt) > maxGainAtTypical) {
      warnings.push({
        severity: 'medium',
        title: 'Half-sell triggers deeper than max gain',
        message: `Half-sell at -${Math.abs(halfSellAt)}% but max gain at typical entry is only ~${maxGainAtTypical.toFixed(0)}%. Consider setting half-sell closer to -${Math.round(maxGainAtTypical)}% for balanced risk/reward.`
      });
    }

    if (letItRide && lirTrail <= 5) {
      tips.push({
        title: 'Tight trail distance',
        message: `Trail distance at ${lirTrail}% is tight — normal price noise can trigger the exit. Consider 8-12% for more room to breathe.`
      });
    }

    if (letItRide && lirTrigger > maxGainAtTypical * 0.8) {
      warnings.push({
        severity: 'medium',
        title: 'Let It Ride trigger may be unreachable',
        message: `Trigger at ${lirTrigger}% but max gain at typical 77¢ entry is ~${maxGainAtTypical.toFixed(0)}%. Auto-cap will reduce it to ~${(maxGainAtTypical * 0.8).toFixed(0)}%. Consider lowering trigger to ${Math.round(maxGainAtTypical * 0.5)}% for earlier activation.`
      });
    }

    if (!letItRide && hardTp > maxGainAtTypical) {
      warnings.push({
        severity: 'high',
        title: 'Hard TP likely unreachable',
        message: `Hard TP at ${hardTp}% but max gain at typical 77¢ entry is only ~${maxGainAtTypical.toFixed(0)}%. Positions may never hit TP. Lower it to ${Math.round(maxGainAtTypical * 0.7)}% or enable Let It Ride.`
      });
    }

    if (aggressive && noSL) {
      warnings.push({
        severity: 'medium',
        title: 'Aggressive Mode + No Stop Losses',
        message: 'Risk limits bypassed AND no stop losses = maximum exposure with minimal protection. Consider disabling one of these.'
      });
    }

    if (noSL && !hedgeEnabled && !halfSellDisabled) {
      tips.push({
        title: 'Consider Hedge & Switch',
        message: 'With No SL, positions only exit at force-sell floor. Enabling Hedge & Switch can recover losses by flipping to the winning side.'
      });
    }

    return { warnings, tips, activeFeatures };
  }, [form?.sentinelMode]);

  const updateNested = (path, value) => {
    setForm((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      const [root, key] = path.split('.');
      next[root] = { ...(next[root] || {}), [key]: value };
      return next;
    });
  };

  const searchMarkets = async () => {
    const q = (marketSearchQuery || '').trim();
    if (!q) {
      setMarketSearchResults([]);
      return;
    }
    setIsMarketSearchLoading(true);
    setMarketSearchError('');
    try {
      const res = await api.get('/markets/search', { params: { q } });
      const list = Array.isArray(res.data) ? res.data : [];
      setMarketSearchResults(list);
    } catch (e) {
      setMarketSearchResults([]);
      setMarketSearchError(e.response?.data?.error || 'Failed to search markets');
    } finally {
      setIsMarketSearchLoading(false);
    }
  };

  const addMarketToWhitelist = (market) => {
    setForm((prev) => {
      if (!prev) return prev;
      const conditionId = (market?.conditionId || market?.condition_id || '').toString().trim();
      if (!conditionId) return prev;
      const next = { ...prev };
      const current = Array.isArray(next.universe?.marketWhitelist) ? next.universe.marketWhitelist : [];
      if (current.some((m) => String(m?.conditionId || '').trim() === conditionId)) return prev;
      const entry = {
        conditionId,
        question: (market?.question || '').toString(),
        slug: (market?.slug || '').toString()
      };
      next.universe = { ...(next.universe || {}), marketWhitelist: [entry, ...current].slice(0, 200) };
      return next;
    });
  };

  const removeMarketFromWhitelist = (conditionId) => {
    setForm((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      const current = Array.isArray(next.universe?.marketWhitelist) ? next.universe.marketWhitelist : [];
      next.universe = {
        ...(next.universe || {}),
        marketWhitelist: current.filter((m) => String(m?.conditionId || '') !== String(conditionId || ''))
      };
      return next;
    });
  };

  const saveConfig = async () => {
    if (!form) return;
    setError('');
    setMessage(null);
    setIsSaving(true);
    try {
      const whitelist = Array.isArray(form.universe?.marketWhitelist)
        ? form.universe.marketWhitelist
            .map((m) => ({
              conditionId: (m?.conditionId ? String(m.conditionId) : '').trim(),
              question: (typeof m?.question === 'string' ? m.question : ''),
              slug: (typeof m?.slug === 'string' ? m.slug : '')
            }))
            .filter((m) => m.conditionId)
        : [];

      const payload = {
        isActive: form.isActive,
        mode: form.mode,
        signalMode: form.signalMode,
        universe: {
          category: form.universe.category || 'all',
          includeSports: Boolean(form.universe.includeSports),
          includeCrypto: Boolean(form.universe.includeCrypto),
          marketWhitelist: whitelist
        },
        timeframe: {
          minHoursToExpiry: Number(form.timeframe.minHoursToExpiry),
          maxHoursToExpiry: Number(form.timeframe.maxHoursToExpiry)
        },
        risk: {
          maxOpenPositions: Number(form.risk.maxOpenPositions),
          maxDailyTrades: Number(form.risk.maxDailyTrades),
          baseTradeAmount: Number(form.risk.baseTradeAmount),
          maxTradeAmount: Number(form.risk.maxTradeAmount),
          maxDailyLoss: Number(form.risk.maxDailyLoss),
          cooldownSeconds: Number(form.risk.cooldownSeconds),
          stopLoss: {
            enabled: Boolean(form.risk.stopLoss?.enabled),
            percentage: Number(form.risk.stopLoss?.percentage || 15),
            trailing: Boolean(form.risk.stopLoss?.trailing)
          },
          takeProfit: {
            enabled: Boolean(form.risk.takeProfit?.enabled),
            percentage: Number(form.risk.takeProfit?.percentage || 25)
          },
          breakEvenStop: {
            enabled: Boolean(form.risk.breakEvenStop?.enabled),
            triggerPercent: Number(form.risk.breakEvenStop?.triggerPercent || 10)
          }
        },
        filters: {
          minLiquidity: Number(form.filters.minLiquidity),
          maxSpread: Number(form.filters.maxSpread),
          minVolume24h: Number(form.filters.minVolume24h)
        },
        signal: {
          threshold: Number(form.signal.threshold),
          lookbackPoints: Number(form.signal.lookbackPoints)
        },
        bamMode: {
          enabled: Boolean(form.bamMode?.enabled),
          overrideMaxPositions: Boolean(form.bamMode?.overrideMaxPositions),
          overrideMaxTrades: Boolean(form.bamMode?.overrideMaxTrades),
          minConfidence: Number(form.bamMode?.minConfidence || 0.85),
          multiplier: Number(form.bamMode?.multiplier || 1.5),
          minDataPoints: Number(form.bamMode?.minDataPoints || 50)
        },
        sentinelMode: {
          enabled: Boolean(form.sentinelMode?.enabled),
          mode: form.sentinelMode?.mode || 'paper', // 'paper' or 'real'
          paperBankroll: Number(form.sentinelMode?.paperBankroll) || 10000,
          maxPortfolioRisk: Number(form.sentinelMode?.maxPortfolioRisk ?? 15),
          maxSingleTradeRisk: Number(form.sentinelMode?.maxSingleTradeRisk ?? 3),
          kellyFraction: Number(form.sentinelMode?.kellyFraction ?? 0.25),
          minEdgeRequired: Math.max(0, Number(form.sentinelMode?.minEdgeRequired) || 0),
          overrideRiskBlocks: Boolean(form.sentinelMode?.overrideRiskBlocks),
          noStopLosses: Boolean(form.sentinelMode?.noStopLosses),
          sellOnlyOver90Cents: Boolean(form.sentinelMode?.sellOnlyOver90Cents),
          noStopLossesMarketFilter: form.sentinelMode?.noStopLossesMarketFilter || 'all',
          lossMitigationEnabled: form.sentinelMode?.lossMitigationEnabled !== false,
          // IMPORTANT: Loss thresholds must be NEGATIVE (e.g., -70 means 70% loss)
          // If user enters positive, convert to negative
          catastrophicLossFloor: (() => {
            const val = Number(form.sentinelMode?.catastrophicLossFloor ?? -70);
            return val > 0 ? -val : val; // 70 becomes -70
          })(),
          halfSellAtLoss: (() => {
            const val = Number(form.sentinelMode?.halfSellAtLoss ?? -50);
            return val > 0 ? -val : val; // 50 becomes -50
          })(),
          hedgeAfterHalfSell: Boolean(form.sentinelMode?.hedgeAfterHalfSell),
          hedgeEnabled: Boolean(form.sentinelMode?.hedgeEnabled),
          hedgeTriggerPercent: (() => {
            const val = Number(form.sentinelMode?.hedgeTriggerPercent ?? -25);
            return val > 0 ? -val : val; // 25 becomes -25
          })(),
          confidenceBasedSizing: form.sentinelMode?.confidenceBasedSizing !== false,
          marketTypeShares: {
            crypto: Number(form.sentinelMode?.marketTypeShares?.crypto ?? 10),
            sports: Number(form.sentinelMode?.marketTypeShares?.sports ?? 10),
            politics: Number(form.sentinelMode?.marketTypeShares?.politics ?? 10),
            other: Number(form.sentinelMode?.marketTypeShares?.other ?? 10)
          },
          perMarketStopLossEnabled: Boolean(form.sentinelMode?.perMarketStopLossEnabled),
          stopLossByMarketType: {
            crypto: Number(form.sentinelMode?.stopLossByMarketType?.crypto ?? 80),
            sports: Number(form.sentinelMode?.stopLossByMarketType?.sports ?? 30),
            politics: Number(form.sentinelMode?.stopLossByMarketType?.politics ?? 40),
            other: Number(form.sentinelMode?.stopLossByMarketType?.other ?? 40)
          },
          tradeSizing: {
            mode: form.sentinelMode?.tradeSizing?.mode || 'kelly',
            usdAmount: Number(form.sentinelMode?.tradeSizing?.usdAmount ?? 1),
            shareAmount: Number(form.sentinelMode?.tradeSizing?.shareAmount ?? 10)
          },
          orderType: form.sentinelMode?.orderType || 'GTC',
          dynamicStopLoss: { enabled: true },
          dynamicTakeProfit: {
            enabled: form.sentinelMode?.dynamicTakeProfit?.enabled !== false,
            basePercent: Number(form.sentinelMode?.dynamicTakeProfit?.basePercent || 25),
            letItRide: Boolean(form.sentinelMode?.dynamicTakeProfit?.letItRide),
            letItRideTrigger: Number(form.sentinelMode?.dynamicTakeProfit?.letItRideTrigger || 15),
            letItRideTrailPercent: Number(form.sentinelMode?.dynamicTakeProfit?.letItRideTrailPercent || 10),
            letItRideStopAtEntry: form.sentinelMode?.dynamicTakeProfit?.letItRideStopAtEntry !== false
          },
          entryRequirements: { minConfidence: 0.65, minSignalAlignment: 3 },
          balanceManagement: { reservePercent: 30 },
          recoveryMode: { enabled: true },
          certaintyScalp: {
            enabled: Boolean(form.sentinelMode?.certaintyScalp?.enabled),
            onlyMode: Boolean(form.sentinelMode?.certaintyScalp?.onlyMode),
            overrideMaxPositions: Boolean(form.sentinelMode?.certaintyScalp?.overrideMaxPositions ?? true),
            maxHoursToExpiry: Number(form.sentinelMode?.certaintyScalp?.maxHoursToExpiry || 24),
            minFavoritePrice: Number(form.sentinelMode?.certaintyScalp?.minFavoritePrice || 0.90),
            tradeAmountMultiplier: Number(form.sentinelMode?.certaintyScalp?.tradeAmountMultiplier || 2.0),
            excludeCrypto: Boolean(form.sentinelMode?.certaintyScalp?.excludeCrypto ?? true),
            stopLossPercent: Number(form.sentinelMode?.certaintyScalp?.stopLossPercent || 20),
            disableTakeProfit: Boolean(form.sentinelMode?.certaintyScalp?.disableTakeProfit ?? true)
          }
        },
        discord: {
          enabled: Boolean(form.discord?.enabled),
          webhookUrl: String(form.discord?.webhookUrl || '').trim()
        },
        ta: {
          minConfluenceScore: Number(form.ta.minConfluenceScore),
          rsiPeriod: Number(form.ta.rsiPeriod),
          emaFast: Number(form.ta.emaFast),
          emaSlow: Number(form.ta.emaSlow),
          lookbackDays: Number(form.ta.lookbackDays)
        },
        execution: {
          orderType: form.execution?.orderType || 'MARKET',
          slippage: Number(form.execution?.slippage || 5)
        }
      };

      const res = await api.post('/whale/config', payload);
      setConfig(res.data);
      setMessage({ type: 'success', text: 'Saved Whale Mode settings.' });
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const startStop = async (action) => {
    setError('');
    setMessage(null);
    setIsToggling(true);
    try {
      const res = await api.post(action === 'start' ? '/whale/start' : '/whale/stop');
      setConfig(res.data?.config || res.data);
      setMessage({ type: 'success', text: action === 'start' ? 'Whale Mode started.' : 'Whale Mode stopped.' });
    } catch (e) {
      setError(e.response?.data?.error || `Failed to ${action} Whale Mode`);
    } finally {
      setIsToggling(false);
    }
  };

  const manualTick = async () => {
    setError('');
    setMessage(null);
    setIsToggling(true);
    try {
      await api.post('/whale/tick');
      setMessage({ type: 'success', text: 'Tick executed.' });
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to tick Whale Mode');
    } finally {
      setIsToggling(false);
    }
  };
  
  const emergencyStop = async () => {
    setError('');
    setMessage(null);
    setIsToggling(true);
    try {
      await api.post('/whale/emergency-stop');
      setConfig(prev => prev ? { ...prev, isActive: false, mode: 'paper' } : prev);
      setMessage({ type: 'success', text: '🚨 Emergency stop executed - Whale Mode stopped and switched to paper mode.' });
      fetchConfig();
    } catch (e) {
      // Even on error, try to update UI
      setConfig(prev => prev ? { ...prev, isActive: false } : prev);
      setMessage({ type: 'warning', text: 'Stop signal sent. Please refresh to confirm.' });
    } finally {
      setIsToggling(false);
    }
  };

  if (isTierLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
      </div>
    );
  }

  if (!isWhale) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Whale Vault</h1>
            <p className="text-dark-400">Advanced features and modes — locked.</p>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-dark-800/70 border border-dark-700 text-sm">
            <Lock className="w-4 h-4 text-amber-400" />
            <span className="text-dark-200">Locked</span>
          </div>
        </div>

        <div className="card card-glow">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-600/30 to-accent-600/20 border border-primary-500/20 flex items-center justify-center">
              <Crown className="w-6 h-6 text-primary-300" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold">Whale-only access</h2>
              <p className="text-dark-400 mt-1">
                Whale Vault is a locked area for high-end automation, research tools, and new trading modes.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full text-xs bg-dark-800 border border-dark-700 text-dark-300">Autonomous strategies</span>
                <span className="px-3 py-1 rounded-full text-xs bg-dark-800 border border-dark-700 text-dark-300">Execution & risk controls</span>
                <span className="px-3 py-1 rounded-full text-xs bg-dark-800 border border-dark-700 text-dark-300">Signal dashboards</span>
              </div>
              <div className="mt-6 flex items-center gap-3">
                <Link to="/pricing" className="btn btn-gradient">
                  Upgrade to Whale
                </Link>
                <Link to="/pricing" className="text-sm text-dark-400 hover:text-primary-300 transition-colors">
                  View plans
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Whale Vault</h1>
          <p className="text-dark-400">Whale Mode ({config?.mode === 'real' ? 'REAL' : 'Paper'}) • Autonomous crypto UP/DOWN strategy</p>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-dark-800/70 border border-dark-700 text-sm">
          <Crown className="w-4 h-4 text-primary-300" />
          <span className="text-dark-200">Whale Tier</span>
        </div>
      </div>

      {(error || message) && (
        <div
          className={`p-4 rounded-xl border text-sm flex items-center gap-2 ${
            error
              ? 'bg-red-500/10 border-red-500/20 text-red-400'
              : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300'
          }`}
        >
          {error ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
          <span>{error || message?.text}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold">Whale Mode Controls</h2>
                <p className="text-sm text-dark-400 mt-1">Start/stop the autonomous engine and tune its parameters.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => startStop((config?.isActive ? 'stop' : 'start'))}
                  disabled={isToggling || isStatusLoading}
                  className={`btn ${config?.isActive ? 'btn-danger' : 'btn-success'} flex items-center gap-2`}
                >
                  {isToggling || isStatusLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : config?.isActive ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  {isStatusLoading ? 'Loading...' : (config?.isActive ? 'Stop' : 'Start')}
                </button>
                <button
                  onClick={manualTick}
                  disabled={isToggling || isStatusLoading}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${isToggling ? 'animate-spin' : ''}`} />
                  Tick
                </button>
                {config?.isActive && (
                  <button
                    onClick={emergencyStop}
                    disabled={isToggling}
                    className="btn bg-red-700 hover:bg-red-600 text-white flex items-center gap-2"
                    title="Emergency stop - bypasses all checks"
                  >
                    🚨 Emergency Stop
                  </button>
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-xl bg-dark-800/60 border border-dark-700">
                <p className="text-xs text-dark-500">Status</p>
                <p className={`text-sm font-semibold mt-1 ${config?.isActive ? 'text-emerald-300' : 'text-dark-200'}`}>
                  {isStatusLoading ? 'Loading' : (config?.isActive ? 'Running' : 'Stopped')}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-dark-800/60 border border-dark-700">
                <p className="text-xs text-dark-500">Universe</p>
                <p className="text-sm font-semibold mt-1">{universeSummary}</p>
              </div>
              <div className={`p-3 rounded-xl border ${config?.mode === 'real' ? 'bg-red-500/10 border-red-500/30' : 'bg-dark-800/60 border-dark-700'}`}>
                <p className="text-xs text-dark-500">Mode</p>
                <p className={`text-sm font-semibold mt-1 ${config?.mode === 'real' ? 'text-red-400' : 'text-dark-200'}`}>
                  {config?.mode === 'real' ? '🔴 REAL' : 'Paper'}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-dark-800/60 border border-dark-700">
                <p className="text-xs text-dark-500">Config Updated</p>
                <p className="text-sm font-semibold mt-1">
                  {config?.updatedAt ? new Date(config.updatedAt).toLocaleString() : '—'}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold">Strategy Settings</h2>
                <p className="text-sm text-dark-400 mt-1">These settings control market selection, risk, and signal strength.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchConfig}
                  disabled={isConfigLoading || isSaving}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  {isConfigLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Refresh
                </button>
                <button
                  onClick={saveConfig}
                  disabled={isSaving || !form}
                  className="btn btn-primary flex items-center gap-2"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save
                </button>
              </div>
            </div>

            {!form ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
              </div>
            ) : (
              <div className="mt-6 space-y-8">
                {/* Trading Mode Selection - IMPORTANT */}
                <div className={`p-4 rounded-xl border ${form.mode === 'real' ? 'bg-red-500/10 border-red-500/30' : 'bg-dark-800/60 border-dark-700'}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-white">Trading Mode</h3>
                      <p className="text-sm text-dark-400 mt-1">
                        {form.mode === 'real' 
                          ? '⚠️ REAL MODE: Trades will execute with real money on Polymarket!'
                          : 'Paper mode: Simulated trades only, no real money at risk.'}
                      </p>
                    </div>
                    <select
                      value={form.mode}
                      onChange={(e) => setForm((prev) => ({ ...prev, mode: e.target.value }))}
                      className={`input w-40 ${form.mode === 'real' ? 'border-red-500 text-red-400' : ''}`}
                    >
                      <option value="paper">Paper</option>
                      <option value="real">🔴 REAL</option>
                    </select>
                  </div>
                  {form.mode === 'real' && (
                    <div className="mt-3 p-3 rounded-lg bg-red-500/20 border border-red-500/40">
                      <p className="text-sm text-red-300 font-medium">
                        ⚠️ Real trading requires a connected wallet with USDC balance. 
                        All trades will be executed on Polymarket with real funds.
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <SelectField
                    label="Signal mode"
                    value={form.signalMode}
                    onChange={(v) =>
                      setForm((prev) => {
                        if (!prev) return prev;
                        const next = { ...prev, signalMode: v };
                        if (v === 'ta') {
                          next.universe = { ...(next.universe || {}), includeCrypto: true };
                        }
                        return next;
                      })
                    }
                    options={[
                      { value: 'microstructure', label: 'Microstructure (orderbook + momentum)' },
                      { value: 'ta', label: 'TA Mode (CoinGecko price action)' }
                    ]}
                    hint="TA mode only trades markets we can confidently map to a coin."
                  />
                  <SelectField
                    label="Market category"
                    value={form.universe.category}
                    onChange={(v) =>
                      setForm((prev) => {
                        if (!prev) return prev;
                        const next = { ...prev };
                        next.universe = { ...(next.universe || {}), category: v };
                        if (v === 'crypto') {
                          next.universe.includeCrypto = true;
                        }
                        return next;
                      })
                    }
                    options={MARKET_CATEGORY_OPTIONS}
                    hint="Which market types to trade"
                  />
                  <div>
                    <label className="label">Include Sports</label>
                    <div className="flex items-center gap-3 mt-2">
                      <input
                        type="checkbox"
                        id="includeSports"
                        checked={form.universe.includeSports}
                        onChange={(e) => updateNested('universe.includeSports', e.target.checked)}
                        className="w-4 h-4 rounded border-dark-600 bg-dark-800 text-primary-500 focus:ring-primary-500"
                      />
                      <label htmlFor="includeSports" className="text-sm text-dark-400">Trade sports markets</label>
                    </div>
                  </div>
                  <div>
                    <label className="label">Include Crypto</label>
                    <div className="flex items-center gap-3 mt-2">
                      <input
                        type="checkbox"
                        id="includeCrypto"
                        checked={form.universe.includeCrypto}
                        disabled={form.universe.category === 'crypto' || form.signalMode === 'ta'}
                        onChange={(e) => updateNested('universe.includeCrypto', e.target.checked)}
                        className="w-4 h-4 rounded border-dark-600 bg-dark-800 text-primary-500 focus:ring-primary-500 disabled:opacity-50"
                      />
                      <label htmlFor="includeCrypto" className="text-sm text-dark-400">
                        Trade crypto markets
                        {(form.universe.category === 'crypto' || form.signalMode === 'ta') ? ' (required)' : ''}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-dark-800/40 border border-dark-700">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-white">Market Selection (Optional)</h4>
                      <p className="text-xs text-dark-500 mt-1">
                        Add markets to trade ONLY those markets. Leave empty to trade the full universe.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateNested('universe.marketWhitelist', [])}
                      disabled={!Array.isArray(form.universe?.marketWhitelist) || form.universe.marketWhitelist.length === 0}
                      className="btn btn-secondary"
                    >
                      Clear
                    </button>
                  </div>

                  <div className="mt-4 flex flex-col md:flex-row gap-2">
                    <input
                      value={marketSearchQuery}
                      onChange={(e) => setMarketSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') searchMarkets();
                      }}
                      className="input flex-1"
                      placeholder="Search markets by question or slug…"
                    />
                    <button
                      type="button"
                      onClick={searchMarkets}
                      disabled={isMarketSearchLoading}
                      className="btn btn-primary"
                    >
                      {isMarketSearchLoading ? 'Searching…' : 'Search'}
                    </button>
                  </div>

                  {marketSearchError ? (
                    <p className="mt-2 text-sm text-red-400">{marketSearchError}</p>
                  ) : null}

                  {marketSearchResults.length > 0 ? (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                      {marketSearchResults.slice(0, 10).map((m) => (
                        <div key={m.conditionId || m.condition_id || m.slug} className="p-3 rounded-lg bg-dark-900/40 border border-dark-700 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm text-dark-200 truncate">{m.question}</p>
                            <p className="text-xs text-dark-500 truncate">{m.slug}</p>
                          </div>
                          <button type="button" onClick={() => addMarketToWhitelist(m)} className="btn btn-secondary">
                            Add
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <div className="mt-4">
                    <p className="text-xs text-dark-500">Selected markets</p>
                    {Array.isArray(form.universe?.marketWhitelist) && form.universe.marketWhitelist.length > 0 ? (
                      <div className="mt-2 space-y-2">
                        {form.universe.marketWhitelist.slice(0, 50).map((m) => (
                          <div key={m.conditionId} className="p-3 rounded-lg bg-dark-900/40 border border-dark-700 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm text-dark-200 truncate">{m.question || m.slug || m.conditionId}</p>
                              <p className="text-xs text-dark-500 truncate">{m.conditionId}</p>
                            </div>
                            <button type="button" onClick={() => removeMarketFromWhitelist(m.conditionId)} className="btn btn-secondary">
                              Remove
                            </button>
                          </div>
                        ))}
                        {form.universe.marketWhitelist.length > 50 ? (
                          <p className="text-xs text-dark-500">Showing first 50 selected markets.</p>
                        ) : null}
                      </div>
                    ) : (
                      <p className="mt-2 text-sm text-dark-400">No markets selected (no restriction).</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <NumberField
                    label="Signal threshold (0.51 - 0.80)"
                    value={form.signal.threshold}
                    onChange={(v) => updateNested('signal.threshold', v)}
                    min={0.51}
                    max={0.8}
                    step={0.01}
                    hint="Higher = fewer, stronger trades."
                  />
                </div>

                {form.signalMode === 'ta' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <NumberField
                      label="Min confluence score (0.50 - 0.95)"
                      value={form.ta.minConfluenceScore}
                      onChange={(v) => updateNested('ta.minConfluenceScore', v)}
                      min={0.5}
                      max={0.95}
                      step={0.01}
                      hint="Higher = fewer trades; requires stronger TA agreement."
                    />
                    <NumberField
                      label="Lookback days (CoinGecko)"
                      value={form.ta.lookbackDays}
                      onChange={(v) => updateNested('ta.lookbackDays', v)}
                      min={1}
                      max={90}
                      step={1}
                    />
                    <NumberField
                      label="RSI period"
                      value={form.ta.rsiPeriod}
                      onChange={(v) => updateNested('ta.rsiPeriod', v)}
                      min={5}
                      max={50}
                      step={1}
                    />
                    <NumberField
                      label="EMA fast / slow"
                      value={form.ta.emaFast}
                      onChange={(v) => updateNested('ta.emaFast', v)}
                      min={3}
                      max={50}
                      step={1}
                    />
                    <NumberField
                      label="EMA slow"
                      value={form.ta.emaSlow}
                      onChange={(v) => updateNested('ta.emaSlow', v)}
                      min={4}
                      max={200}
                      step={1}
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <NumberField
                    label="Min hours to expiry"
                    value={form.timeframe.minHoursToExpiry}
                    onChange={(v) => updateNested('timeframe.minHoursToExpiry', v)}
                    min={0}
                    max={720}
                    step={1}
                    hint="Skip markets expiring sooner than this. 0 = trade any market. Higher = avoid short-term volatility."
                  />
                  <NumberField
                    label="Max hours to expiry"
                    value={form.timeframe.maxHoursToExpiry}
                    onChange={(v) => updateNested('timeframe.maxHoursToExpiry', v)}
                    min={0}
                    max={720}
                    step={1}
                    hint="Skip markets expiring later than this. Lower = focus on near-term markets with faster resolution."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <NumberField
                    label="Max open positions"
                    value={form.risk.maxOpenPositions}
                    onChange={(v) => updateNested('risk.maxOpenPositions', v)}
                    min={1}
                    max={20}
                    step={1}
                    hint="How many positions can be open at once. Lower = less risk exposure, higher = more opportunities."
                  />
                  <NumberField
                    label="Max daily trades"
                    value={form.risk.maxDailyTrades}
                    onChange={(v) => updateNested('risk.maxDailyTrades', v)}
                    min={1}
                    max={50}
                    step={1}
                    hint="Auto-stops trading after this many trades per day. Prevents overtrading during volatile days."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <NumberField
                    label="Cooldown seconds"
                    value={form.risk.cooldownSeconds}
                    onChange={(v) => updateNested('risk.cooldownSeconds', v)}
                    min={60}
                    max={3600}
                    step={30}
                    hint="Minimum wait time before trading the same market again. Prevents rapid re-entry after exits."
                  />
                </div>

                <div className={`relative grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-xl ${form.sentinelMode?.enabled ? 'bg-dark-800/40 border border-cyan-500/20' : ''}`}>
                  {form.sentinelMode?.enabled && (
                    <div className="absolute -top-3 left-4 px-2 py-0.5 bg-cyan-900/80 border border-cyan-500/30 rounded text-xs text-cyan-300 font-medium">
                      🤖 Kelly Criterion Active
                    </div>
                  )}
                  <NumberField
                    label="Base trade amount (USDC)"
                    value={form.risk.baseTradeAmount}
                    onChange={(v) => updateNested('risk.baseTradeAmount', v)}
                    min={1}
                    max={100000}
                    step={1}
                    hint={form.sentinelMode?.enabled ? "Sentinel uses Kelly sizing instead" : "Default $ amount per trade. Actual size may vary based on signal strength."}
                  />
                  <NumberField
                    label="Max trade amount (USDC)"
                    value={form.risk.maxTradeAmount}
                    onChange={(v) => updateNested('risk.maxTradeAmount', v)}
                    min={1}
                    max={100000}
                    step={1}
                    hint={form.sentinelMode?.enabled ? "Used as bankroll for Kelly sizing. Should be your total trading capital." : "Maximum $ per single trade. Caps position size regardless of signal."}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <NumberField
                    label="Max daily loss (USDC)"
                    value={form.risk.maxDailyLoss}
                    onChange={(v) => updateNested('risk.maxDailyLoss', v)}
                    min={1}
                    max={1000000}
                    step={1}
                    hint="Safety limit - if total daily losses exceed this, all trading stops until midnight UTC. Protects against bad days."
                  />
                  <NumberField
                    label="Lookback points"
                    value={form.signal.lookbackPoints}
                    onChange={(v) => updateNested('signal.lookbackPoints', v)}
                    min={5}
                    max={240}
                    step={1}
                    hint="Price history buffer size for trend analysis. More points = smoother signals but slower reaction."
                  />
                </div>

                {/* Stop Loss & Take Profit Settings */}
                <div className={`relative p-4 rounded-xl bg-dark-800/60 border border-dark-700 ${form.sentinelMode?.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                  {form.sentinelMode?.enabled && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-dark-900/60 rounded-xl backdrop-blur-sm">
                      <div className="text-center p-4">
                        <span className="text-2xl">🤖</span>
                        <p className="text-cyan-300 font-semibold mt-2">Controlled by Sentinel</p>
                        <p className="text-cyan-400/70 text-xs">Dynamic SL/TP active</p>
                      </div>
                    </div>
                  )}
                  <h3 className="font-semibold text-white mb-4">Stop Loss & Take Profit</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="slEnabled"
                          checked={form.risk.stopLoss?.enabled || false}
                          onChange={(e) => setForm((prev) => ({
                            ...prev,
                            risk: {
                              ...prev.risk,
                              stopLoss: { ...prev.risk.stopLoss, enabled: e.target.checked }
                            }
                          }))}
                          className="w-4 h-4 rounded border-dark-600 bg-dark-800 text-primary-500 focus:ring-primary-500"
                        />
                        <label htmlFor="slEnabled" className="text-sm font-medium">Enable Stop Loss</label>
                      </div>
                      {form.risk.stopLoss?.enabled && (
                        <>
                          <NumberField
                            label="Stop Loss %"
                            value={form.risk.stopLoss?.percentage || 15}
                            onChange={(v) => setForm((prev) => ({
                              ...prev,
                              risk: {
                                ...prev.risk,
                                stopLoss: { ...prev.risk.stopLoss, percentage: Number(v) }
                              }
                            }))}
                            min={1}
                            max={100}
                            step={1}
                            hint="Auto-sell if position drops by this %"
                          />
                          <div className="flex items-center gap-3 mt-2">
                            <input
                              type="checkbox"
                              id="slTrailing"
                              checked={form.risk.stopLoss?.trailing || false}
                              onChange={(e) => setForm((prev) => ({
                                ...prev,
                                risk: {
                                  ...prev.risk,
                                  stopLoss: { ...prev.risk.stopLoss, trailing: e.target.checked }
                                }
                              }))}
                              className="w-4 h-4 rounded border-dark-600 bg-dark-800 text-primary-500 focus:ring-primary-500"
                            />
                            <label htmlFor="slTrailing" className="text-xs text-dark-400">Trailing (locks in gains)</label>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="tpEnabled"
                          checked={form.risk.takeProfit?.enabled || false}
                          onChange={(e) => setForm((prev) => ({
                            ...prev,
                            risk: {
                              ...prev.risk,
                              takeProfit: { ...prev.risk.takeProfit, enabled: e.target.checked }
                            }
                          }))}
                          className="w-4 h-4 rounded border-dark-600 bg-dark-800 text-primary-500 focus:ring-primary-500"
                        />
                        <label htmlFor="tpEnabled" className="text-sm font-medium">Enable Take Profit</label>
                      </div>
                      {form.risk.takeProfit?.enabled && (
                        <NumberField
                          label="Take Profit %"
                          value={form.risk.takeProfit?.percentage || 25}
                          onChange={(v) => setForm((prev) => ({
                            ...prev,
                            risk: {
                              ...prev.risk,
                              takeProfit: { ...prev.risk.takeProfit, percentage: Number(v) }
                            }
                          }))}
                          min={1}
                          max={500}
                          step={1}
                          hint="Auto-sell if position gains by this %"
                        />
                      )}
                    </div>
                  </div>
                  
                  {/* Break-Even Stop */}
                  <div className="mt-4 pt-4 border-t border-dark-700">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="beEnabled"
                        checked={form.risk.breakEvenStop?.enabled || false}
                        onChange={(e) => setForm((prev) => ({
                          ...prev,
                          risk: {
                            ...prev.risk,
                            breakEvenStop: { ...prev.risk.breakEvenStop, enabled: e.target.checked }
                          }
                        }))}
                        className="w-4 h-4 rounded border-dark-600 bg-dark-800 text-primary-500 focus:ring-primary-500"
                      />
                      <label htmlFor="beEnabled" className="text-sm font-medium">Break-Even Stop</label>
                    </div>
                    {form.risk.breakEvenStop?.enabled && (
                      <div className="mt-3 ml-7">
                        <NumberField
                          label="Trigger at % profit"
                          value={form.risk.breakEvenStop?.triggerPercent || 10}
                          onChange={(v) => setForm((prev) => ({
                            ...prev,
                            risk: {
                              ...prev.risk,
                              breakEvenStop: { ...prev.risk.breakEvenStop, triggerPercent: Number(v) }
                            }
                          }))}
                          min={1}
                          max={100}
                          step={1}
                          hint="Move stop loss to entry price after this % gain"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* BAM! Mode - Ultra High-Confidence Trades */}
                <div className="p-4 rounded-xl bg-gradient-to-r from-amber-900/30 to-orange-900/30 border border-amber-700/50">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">💥</span>
                    <h3 className="font-semibold text-amber-400">BAM! Mode</h3>
                    <span className="text-xs text-amber-500/70 bg-amber-900/30 px-2 py-0.5 rounded">SOTA Algorithm</span>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">
                    Ultra high-confidence trades using historical pattern analysis. When enabled, BAM! signals can override position limits for exceptional opportunities.
                  </p>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <input
                      type="checkbox"
                      id="bamEnabled"
                      checked={form.bamMode?.enabled || false}
                      onChange={(e) => setForm((prev) => ({
                        ...prev,
                        bamMode: { ...prev.bamMode, enabled: e.target.checked }
                      }))}
                      className="w-4 h-4 rounded border-amber-600 bg-dark-800 text-amber-500 focus:ring-amber-500"
                    />
                    <label htmlFor="bamEnabled" className="text-sm font-medium text-amber-300">Enable BAM! Mode</label>
                  </div>
                  
                  {form.bamMode?.enabled && (
                    <div className="space-y-4 pl-7 border-l-2 border-amber-700/50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <NumberField
                          label="Min Confidence %"
                          value={(form.bamMode?.minConfidence || 0.85) * 100}
                          onChange={(v) => setForm((prev) => ({
                            ...prev,
                            bamMode: { ...prev.bamMode, minConfidence: Number(v) / 100 }
                          }))}
                          min={70}
                          max={99}
                          step={1}
                          hint="Minimum confidence for BAM! signal (85% = strong)"
                        />
                        <NumberField
                          label="Trade Multiplier"
                          value={form.bamMode?.multiplier || 1.5}
                          onChange={(v) => setForm((prev) => ({
                            ...prev,
                            bamMode: { ...prev.bamMode, multiplier: Number(v) }
                          }))}
                          min={1}
                          max={5}
                          step={0.25}
                          hint="Multiply trade size for BAM! trades"
                        />
                      </div>
                      
                      <div className="flex flex-wrap gap-4">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="bamOverridePositions"
                            checked={form.bamMode?.overrideMaxPositions ?? true}
                            onChange={(e) => setForm((prev) => ({
                              ...prev,
                              bamMode: { ...prev.bamMode, overrideMaxPositions: e.target.checked }
                            }))}
                            className="w-4 h-4 rounded border-amber-600 bg-dark-800 text-amber-500 focus:ring-amber-500"
                          />
                          <label htmlFor="bamOverridePositions" className="text-sm text-gray-300">Override Max Positions</label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="bamOverrideTrades"
                            checked={form.bamMode?.overrideMaxTrades ?? true}
                            onChange={(e) => setForm((prev) => ({
                              ...prev,
                              bamMode: { ...prev.bamMode, overrideMaxTrades: e.target.checked }
                            }))}
                            className="w-4 h-4 rounded border-amber-600 bg-dark-800 text-amber-500 focus:ring-amber-500"
                          />
                          <label htmlFor="bamOverrideTrades" className="text-sm text-gray-300">Override Max Daily Trades</label>
                        </div>
                      </div>
                      
                      <NumberField
                        label="Min Data Points"
                        value={form.bamMode?.minDataPoints || 50}
                        onChange={(v) => setForm((prev) => ({
                          ...prev,
                          bamMode: { ...prev.bamMode, minDataPoints: Number(v) }
                        }))}
                        min={10}
                        max={200}
                        step={10}
                        hint="Minimum historical snapshots needed for BAM! analysis"
                      />
                    </div>
                  )}
                </div>

                {/* ═══════════════════════════════════════════════════════════════════════════════════ */}
                {/* SENTINEL MODE - The Ultimate Autonomous AI Trading System */}
                {/* ═══════════════════════════════════════════════════════════════════════════════════ */}
                <div className={`relative overflow-hidden rounded-2xl ${form.sentinelMode?.enabled ? 'ring-2 ring-cyan-400 shadow-2xl shadow-cyan-500/30' : ''}`}>
                  {/* Animated gradient background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-cyan-950 to-blue-950"></div>
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-cyan-500/20 via-transparent to-transparent"></div>
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-transparent"></div>
                  
                  {/* Grid pattern overlay */}
                  <div className="absolute inset-0 opacity-5" style={{backgroundImage: 'linear-gradient(rgba(6,182,212,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,0.3) 1px, transparent 1px)', backgroundSize: '20px 20px'}}></div>
                  
                  <div className="relative p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg ${form.sentinelMode?.enabled ? 'animate-pulse' : ''}`}>
                            <span className="text-3xl">🤖</span>
                          </div>
                          {form.sentinelMode?.enabled && (
                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-slate-900 animate-pulse"></div>
                          )}
                        </div>
                        <div>
                          <h3 className="text-2xl font-black bg-gradient-to-r from-cyan-300 via-blue-300 to-cyan-300 bg-clip-text text-transparent">
                            SENTINEL MODE
                          </h3>
                          <p className="text-cyan-400/80 text-sm font-medium">Autonomous AI Trading System</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/50 rounded-full text-amber-300">
                          ⚡ ALPHA
                        </span>
                        {form.sentinelMode?.enabled && (
                          <span className="px-3 py-1 text-xs font-bold bg-green-500/20 border border-green-500/50 rounded-full text-green-300 animate-pulse">
                            ● ACTIVE
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Tagline */}
                    <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20">
                      <p className="text-lg text-white font-semibold text-center">
                        🎯 <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">The World's Most Advanced Prediction Market Trader</span>
                      </p>
                      <p className="text-center text-cyan-300/70 text-sm mt-1">
                        Powered by Kelly Criterion • Multi-Factor Signals • Dynamic Risk Management
                      </p>
                    </div>
                    
                    {/* 🎛️ Sentinel Controls - Feature Showcase */}
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-lg">🎛️</span>
                        <h4 className="text-cyan-200 font-bold">Sentinel Controls</h4>
                        <span className="text-xs text-cyan-500/60 ml-auto">Fully Autonomous • Zero Config Required</span>
                      </div>
                      
                      {/* Primary Feature Cards - 2x3 Grid */}
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                        <div className="group p-4 rounded-xl bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border border-cyan-500/30 hover:border-cyan-400/50 transition-all hover:scale-[1.02]">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">🎯</span>
                            <span className="text-cyan-300 font-bold text-sm">Entry Signal Analysis</span>
                          </div>
                          <div className="text-cyan-400/60 text-xs leading-relaxed">
                            Multi-factor signal validation with momentum, trend, and volatility confirmation. Requires 65%+ confidence.
                          </div>
                        </div>
                        
                        <div className="group p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/30 hover:border-blue-400/50 transition-all hover:scale-[1.02]">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">📊</span>
                            <span className="text-blue-300 font-bold text-sm">Position Sizing (Kelly)</span>
                          </div>
                          <div className="text-blue-400/60 text-xs leading-relaxed">
                            Quarter-Kelly criterion for mathematically optimal bet sizing. Balances growth vs. risk of ruin.
                          </div>
                        </div>
                        
                        <div className="group p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/30 hover:border-emerald-400/50 transition-all hover:scale-[1.02]">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">🛡️</span>
                            <span className="text-emerald-300 font-bold text-sm">Dynamic Stop Losses</span>
                          </div>
                          <div className="text-emerald-400/60 text-xs leading-relaxed">
                            Volatility-adjusted stops with trailing protection. Tightens in calm markets, widens in volatile ones.
                          </div>
                        </div>
                        
                        <div className="group p-4 rounded-xl bg-gradient-to-br from-violet-500/10 to-violet-600/5 border border-violet-500/30 hover:border-violet-400/50 transition-all hover:scale-[1.02]">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">💰</span>
                            <span className="text-violet-300 font-bold text-sm">Take Profit Levels</span>
                          </div>
                          <div className="text-violet-400/60 text-xs leading-relaxed">
                            Scale-out profit taking at multiple levels. Lets winners run with trailing TP after first exit.
                          </div>
                        </div>
                        
                        <div className="group p-4 rounded-xl bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/30 hover:border-amber-400/50 transition-all hover:scale-[1.02]">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">💼</span>
                            <span className="text-amber-300 font-bold text-sm">Balance Management</span>
                          </div>
                          <div className="text-amber-400/60 text-xs leading-relaxed">
                            Portfolio risk limits, max position exposure, and daily loss protection. Never overextends.
                          </div>
                        </div>
                        
                        <div className="group p-4 rounded-xl bg-gradient-to-br from-rose-500/10 to-rose-600/5 border border-rose-500/30 hover:border-rose-400/50 transition-all hover:scale-[1.02]">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">🔄</span>
                            <span className="text-rose-300 font-bold text-sm">Hedge & Switch</span>
                          </div>
                          <div className="text-rose-400/60 text-xs leading-relaxed">
                            Detects when to flip sides. If momentum reverses, closes losing position and opens opposite.
                          </div>
                        </div>
                      </div>
                      
                      {/* Advanced Features - Horizontal scroll on mobile */}
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-sm">⚡</span>
                        <span className="text-cyan-300/80 font-semibold text-xs">SOTA Features</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div className="p-3 rounded-lg bg-slate-800/60 border border-slate-700/50 text-center">
                          <div className="text-lg mb-1">📈</div>
                          <div className="text-white/80 text-xs font-medium">Momentum Analysis</div>
                          <div className="text-slate-400 text-[10px]">Trend strength & acceleration</div>
                        </div>
                        <div className="p-3 rounded-lg bg-slate-800/60 border border-slate-700/50 text-center">
                          <div className="text-lg mb-1">📚</div>
                          <div className="text-white/80 text-xs font-medium">Order Book Reading</div>
                          <div className="text-slate-400 text-[10px]">Bid/ask imbalance signals</div>
                        </div>
                        <div className="p-3 rounded-lg bg-slate-800/60 border border-slate-700/50 text-center">
                          <div className="text-lg mb-1">⏱️</div>
                          <div className="text-white/80 text-xs font-medium">Time Decay Aware</div>
                          <div className="text-slate-400 text-[10px]">Exits before expiry</div>
                        </div>
                        <div className="p-3 rounded-lg bg-slate-800/60 border border-slate-700/50 text-center">
                          <div className="text-lg mb-1">🔄</div>
                          <div className="text-white/80 text-xs font-medium">GTC Order Tracking</div>
                          <div className="text-slate-400 text-[10px]">Persistent order management</div>
                        </div>
                      </div>
                    </div>

                    {/* ═══════════════════════════════════════════════════════════════════════════════════ */}
                    {/* TAKE PROFIT & LET IT RIDE MODE */}
                    {/* ═══════════════════════════════════════════════════════════════════════════════════ */}
                    <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-violet-900/30 to-purple-900/30 border border-violet-500/40">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-2xl">🎰</span>
                        <div>
                          <h4 className="font-bold text-violet-300">Take Profit & Let It Ride</h4>
                          <p className="text-xs text-violet-400/70">Control how profits are taken — hard exit or trailing stop from peak</p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        {/* Hard TP % */}
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Hard Take Profit %</label>
                          <input
                            type="number"
                            value={form.sentinelMode?.dynamicTakeProfit?.basePercent || 25}
                            onChange={(e) => setForm((prev) => ({
                              ...prev,
                              sentinelMode: {
                                ...prev.sentinelMode,
                                dynamicTakeProfit: { ...prev.sentinelMode?.dynamicTakeProfit, basePercent: Number(e.target.value) }
                              }
                            }))}
                            min={5}
                            max={200}
                            className="w-full px-3 py-2 rounded-lg bg-dark-800 border border-gray-700 text-white"
                          />
                          <p className="text-xs text-gray-500 mt-1">Auto-exit at this % profit (default 25%). Ignored when Let It Ride is ON.</p>
                        </div>

                        {/* Let It Ride Toggle */}
                        <div className="p-3 rounded-lg bg-violet-900/20 border border-violet-600/30">
                          <div className="flex items-center gap-3 mb-3">
                            <input
                              type="checkbox"
                              id="letItRide"
                              checked={form.sentinelMode?.dynamicTakeProfit?.letItRide || false}
                              onChange={(e) => setForm((prev) => ({
                                ...prev,
                                sentinelMode: {
                                  ...prev.sentinelMode,
                                  dynamicTakeProfit: { ...prev.sentinelMode?.dynamicTakeProfit, letItRide: e.target.checked }
                                }
                              }))}
                              className="w-4 h-4 rounded border-violet-600 bg-dark-800 text-violet-500 focus:ring-violet-500"
                            />
                            <div>
                              <label htmlFor="letItRide" className="text-sm font-bold text-violet-200">Let It Ride Mode</label>
                              <p className="text-xs text-violet-400/70">No hard TP — once profit hits trigger %, set trailing stop from peak with breakeven floor</p>
                            </div>
                          </div>

                          {form.sentinelMode?.dynamicTakeProfit?.letItRide && (
                            <div className="space-y-3 pl-7 border-l-2 border-violet-700/50">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-300 mb-1">Trigger %</label>
                                  <input
                                    type="number"
                                    value={form.sentinelMode?.dynamicTakeProfit?.letItRideTrigger || 15}
                                    onChange={(e) => setForm((prev) => ({
                                      ...prev,
                                      sentinelMode: {
                                        ...prev.sentinelMode,
                                        dynamicTakeProfit: { ...prev.sentinelMode?.dynamicTakeProfit, letItRideTrigger: Number(e.target.value) }
                                      }
                                    }))}
                                    min={5}
                                    max={100}
                                    className="w-full px-3 py-2 rounded-lg bg-dark-800 border border-gray-700 text-white"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">Activate trailing stop after this % profit</p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-300 mb-1">Trail Distance %</label>
                                  <input
                                    type="number"
                                    value={form.sentinelMode?.dynamicTakeProfit?.letItRideTrailPercent || 10}
                                    onChange={(e) => setForm((prev) => ({
                                      ...prev,
                                      sentinelMode: {
                                        ...prev.sentinelMode,
                                        dynamicTakeProfit: { ...prev.sentinelMode?.dynamicTakeProfit, letItRideTrailPercent: Number(e.target.value) }
                                      }
                                    }))}
                                    min={3}
                                    max={50}
                                    className="w-full px-3 py-2 rounded-lg bg-dark-800 border border-gray-700 text-white"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">Exit if price drops this % from peak</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  id="letItRideStopAtEntry"
                                  checked={form.sentinelMode?.dynamicTakeProfit?.letItRideStopAtEntry !== false}
                                  onChange={(e) => setForm((prev) => ({
                                    ...prev,
                                    sentinelMode: {
                                      ...prev.sentinelMode,
                                      dynamicTakeProfit: { ...prev.sentinelMode?.dynamicTakeProfit, letItRideStopAtEntry: e.target.checked }
                                    }
                                  }))}
                                  className="w-4 h-4 rounded border-violet-600 bg-dark-800 text-violet-500 focus:ring-violet-500"
                                />
                                <label htmlFor="letItRideStopAtEntry" className="text-sm text-gray-300">Breakeven Floor (stop can never go below entry price)</label>
                              </div>
                              <div className="p-2 rounded bg-violet-900/30 text-xs text-violet-300/80">
                                Example: At {form.sentinelMode?.dynamicTakeProfit?.letItRideTrigger || 15}% profit, trailing stop activates. 
                                If price peaks then drops {form.sentinelMode?.dynamicTakeProfit?.letItRideTrailPercent || 10}% from peak → exit.
                                {form.sentinelMode?.dynamicTakeProfit?.letItRideStopAtEntry !== false && ' Stop never goes below entry (breakeven guaranteed).'}
                              </div>
                              <div className="p-2 rounded bg-amber-900/30 text-xs text-amber-300/80 mt-1">
                                ⚠️ <strong>Polymarket math:</strong> All bets settle at $1. Max gain at 75¢ entry = 33%, at 80¢ = 25%, at 85¢ = 17.6%. 
                                The bot auto-caps your trigger at 80% of max possible gain so it actually fires.
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ═══════════════════════════════════════════════════════════════════════════════════ */}
                    {/* CERTAINTY SCALP MODE - Low gain, high probability trades */}
                    {/* ═══════════════════════════════════════════════════════════════════════════════════ */}
                    <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/40">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-2xl">💰</span>
                        <div>
                          <h4 className="font-bold text-green-300">Certainty Scalp Mode</h4>
                          <p className="text-xs text-green-400/70">Low-gain, high-probability trades on almost-certain outcomes</p>
                        </div>
                        <span className="ml-auto px-2 py-0.5 text-xs font-bold bg-green-500/20 border border-green-500/50 rounded-full text-green-300">
                          NEW
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-400 mb-4">
                        <strong>Buy the GUARANTEED WINNER</strong> - favorites at ≥90¢ that expire within 24 hours. 
                        Let it ride to resolution (no TP) with 20% SL protection against rare upsets.
                        <span className="text-red-400 font-semibold"> NO CRYPTO</span> - they're too volatile for certainty plays.
                      </p>
                      
                      <div className="flex items-center gap-3 mb-4">
                        <input
                          type="checkbox"
                          id="certaintyEnabled"
                          checked={form.sentinelMode?.certaintyScalp?.enabled || false}
                          onChange={(e) => setForm((prev) => ({
                            ...prev,
                            sentinelMode: { 
                              ...prev.sentinelMode, 
                              certaintyScalp: { ...prev.sentinelMode?.certaintyScalp, enabled: e.target.checked }
                            }
                          }))}
                          className="w-4 h-4 rounded border-green-600 bg-dark-800 text-green-500 focus:ring-green-500"
                        />
                        <label htmlFor="certaintyEnabled" className="text-sm font-medium text-green-300">Enable Certainty Scalp Mode</label>
                      </div>
                      
                      {form.sentinelMode?.certaintyScalp?.enabled && (
                        <div className="space-y-4 pl-7 border-l-2 border-green-700/50">
                          {/* Only Mode Toggle */}
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-green-900/20 border border-green-600/30">
                            <input
                              type="checkbox"
                              id="certaintyOnlyMode"
                              checked={form.sentinelMode?.certaintyScalp?.onlyMode || false}
                              onChange={(e) => setForm((prev) => ({
                                ...prev,
                                sentinelMode: { 
                                  ...prev.sentinelMode, 
                                  certaintyScalp: { ...prev.sentinelMode?.certaintyScalp, onlyMode: e.target.checked }
                                }
                              }))}
                              className="w-4 h-4 rounded border-green-600 bg-dark-800 text-green-500 focus:ring-green-500"
                            />
                            <div>
                              <label htmlFor="certaintyOnlyMode" className="text-sm font-bold text-green-200">ONLY Trade Certainty Scalps</label>
                              <p className="text-xs text-green-400/70">Ignore all other signals - exclusively trade certainty opportunities</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">Max Hours to Expiry</label>
                              <input
                                type="number"
                                value={form.sentinelMode?.certaintyScalp?.maxHoursToExpiry || 24}
                                onChange={(e) => setForm((prev) => ({
                                  ...prev,
                                  sentinelMode: { 
                                    ...prev.sentinelMode, 
                                    certaintyScalp: { ...prev.sentinelMode?.certaintyScalp, maxHoursToExpiry: Number(e.target.value) }
                                  }
                                }))}
                                min={1}
                                max={48}
                                className="w-full px-3 py-2 rounded-lg bg-dark-800 border border-gray-700 text-white"
                              />
                              <p className="text-xs text-gray-500 mt-1">Only trade if market expires within this many hours</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">Trade Amount Multiplier</label>
                              <input
                                type="number"
                                value={form.sentinelMode?.certaintyScalp?.tradeAmountMultiplier || 2.0}
                                onChange={(e) => setForm((prev) => ({
                                  ...prev,
                                  sentinelMode: { 
                                    ...prev.sentinelMode, 
                                    certaintyScalp: { ...prev.sentinelMode?.certaintyScalp, tradeAmountMultiplier: Number(e.target.value) }
                                  }
                                }))}
                                min={0.5}
                                max={5}
                                step={0.5}
                                className="w-full px-3 py-2 rounded-lg bg-dark-800 border border-gray-700 text-white"
                              />
                              <p className="text-xs text-gray-500 mt-1">Multiply normal trade size for certainty trades (2x = double)</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">Min Favorite Price</label>
                              <input
                                type="number"
                                value={(form.sentinelMode?.certaintyScalp?.minFavoritePrice || 0.90) * 100}
                                onChange={(e) => setForm((prev) => ({
                                  ...prev,
                                  sentinelMode: { 
                                    ...prev.sentinelMode, 
                                    certaintyScalp: { ...prev.sentinelMode?.certaintyScalp, minFavoritePrice: Number(e.target.value) / 100 }
                                  }
                                }))}
                                min={90}
                                max={99}
                                className="w-full px-3 py-2 rounded-lg bg-dark-800 border border-gray-700 text-white"
                              />
                              <p className="text-xs text-gray-500 mt-1">Only buy WINNERS at ≥{(form.sentinelMode?.certaintyScalp?.minFavoritePrice || 0.90) * 100}¢</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">Stop Loss %</label>
                              <input
                                type="number"
                                value={form.sentinelMode?.certaintyScalp?.stopLossPercent || 20}
                                onChange={(e) => setForm((prev) => ({
                                  ...prev,
                                  sentinelMode: { 
                                    ...prev.sentinelMode, 
                                    certaintyScalp: { ...prev.sentinelMode?.certaintyScalp, stopLossPercent: Number(e.target.value) }
                                  }
                                }))}
                                min={10}
                                max={50}
                                className="w-full px-3 py-2 rounded-lg bg-dark-800 border border-gray-700 text-white"
                              />
                              <p className="text-xs text-gray-500 mt-1">Protect against rare upsets (20% recommended)</p>
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap gap-4">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id="certaintyDisableTP"
                                checked={form.sentinelMode?.certaintyScalp?.disableTakeProfit ?? true}
                                onChange={(e) => setForm((prev) => ({
                                  ...prev,
                                  sentinelMode: { 
                                    ...prev.sentinelMode, 
                                    certaintyScalp: { ...prev.sentinelMode?.certaintyScalp, disableTakeProfit: e.target.checked }
                                  }
                                }))}
                                className="w-4 h-4 rounded border-green-600 bg-dark-800 text-green-500 focus:ring-green-500"
                              />
                              <label htmlFor="certaintyDisableTP" className="text-sm text-gray-300">Let It Ride (No Take Profit)</label>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id="certaintyOverridePositions"
                                checked={form.sentinelMode?.certaintyScalp?.overrideMaxPositions ?? true}
                                onChange={(e) => setForm((prev) => ({
                                  ...prev,
                                  sentinelMode: { 
                                    ...prev.sentinelMode, 
                                    certaintyScalp: { ...prev.sentinelMode?.certaintyScalp, overrideMaxPositions: e.target.checked }
                                  }
                                }))}
                                className="w-4 h-4 rounded border-green-600 bg-dark-800 text-green-500 focus:ring-green-500"
                              />
                              <label htmlFor="certaintyOverridePositions" className="text-sm text-gray-300">Override Max Positions</label>
                            </div>
                            <div className="flex items-center gap-2 opacity-50">
                              <span className="text-red-400 text-sm">🚫 Crypto always excluded</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Important Rules Banner */}
                    <div className="mb-4 p-4 rounded-xl bg-gradient-to-r from-red-900/40 to-rose-900/30 border border-red-500/40">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-5 h-5 text-red-400" />
                        <p className="text-red-200 font-bold">⛔ IMPORTANT RULES</p>
                      </div>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <span className="text-red-400 font-bold">1.</span>
                          <span className="text-red-300/90"><strong>DO NOT manually close positions</strong> while Sentinel is running - it will re-open them. Disable Sentinel first, then close.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-red-400 font-bold">2.</span>
                          <span className="text-red-300/90"><strong>DO NOT trade the same markets</strong> manually on Polymarket while Sentinel is active - this causes conflicts.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-red-400 font-bold">3.</span>
                          <span className="text-red-300/90"><strong>Always click SAVE</strong> after changing settings - changes don't apply until saved.</span>
                        </li>
                      </ul>
                    </div>
                    
                    {/* How It Works Banner */}
                    <div className="mb-4 p-4 rounded-xl bg-gradient-to-r from-cyan-900/40 to-blue-900/30 border border-cyan-500/40">
                      <div className="flex items-center gap-2 mb-3">
                        <Activity className="w-5 h-5 text-cyan-400" />
                        <p className="text-cyan-200 font-bold">📖 HOW IT WORKS</p>
                      </div>
                      <ul className="space-y-2 text-sm text-cyan-300/80">
                        <li className="flex items-start gap-2">
                          <span className="text-cyan-400">•</span>
                          <span><strong>Paper Mode:</strong> Simulates trades without real money. Great for testing. No wallet needed.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-cyan-400">•</span>
                          <span><strong>Real Mode:</strong> Executes real trades with your USDC. Requires connected + funded wallet.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-cyan-400">•</span>
                          <span><strong>Hedge & Switch:</strong> If a trade starts losing and momentum flips, Sentinel can close and open the opposite side.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-cyan-400">•</span>
                          <span><strong>Position Sync:</strong> Sentinel monitors ALL your positions across both wallet addresses every 5 minutes.</span>
                        </li>
                      </ul>
                    </div>
                    
                    {/* Warning Banner */}
                    <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-amber-900/40 to-orange-900/30 border border-amber-500/40 flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-amber-200 font-bold">⚠️ Alpha Feature - Full Autonomous Control</p>
                        <p className="text-amber-300/70 text-sm mt-1">
                          When enabled, Sentinel takes <strong>complete control</strong> of all trading decisions. 
                          It will override your manual settings for stop losses, take profits, and position sizes 
                          using its advanced algorithms. Only enable if you trust AI to manage your capital.
                        </p>
                      </div>
                    </div>

                    {/* Main Toggle - Big and Prominent */}
                    <div className={`p-5 rounded-xl mb-6 transition-all ${form.sentinelMode?.enabled ? 'bg-gradient-to-r from-cyan-600/30 to-blue-600/30 border-2 border-cyan-400' : 'bg-slate-800/50 border border-slate-700 hover:border-cyan-500/50'}`}>
                      <label htmlFor="sentinelEnabled" className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center gap-4">
                          <div className={`w-16 h-9 rounded-full p-1 transition-all ${form.sentinelMode?.enabled ? 'bg-gradient-to-r from-cyan-500 to-blue-500' : 'bg-slate-700'}`}>
                            <div className={`w-7 h-7 rounded-full bg-white shadow-lg transition-all ${form.sentinelMode?.enabled ? 'translate-x-7' : 'translate-x-0'}`}></div>
                          </div>
                          <div>
                            <div className={`text-lg font-bold ${form.sentinelMode?.enabled ? 'text-cyan-200' : 'text-gray-300'}`}>
                              {form.sentinelMode?.enabled ? '🟢 SENTINEL ACTIVE' : 'Enable Sentinel Mode'}
                            </div>
                            <div className={`text-sm ${form.sentinelMode?.enabled ? 'text-cyan-400/80' : 'text-gray-500'}`}>
                              {form.sentinelMode?.enabled ? 'AI is now controlling all trading decisions' : 'Click to activate autonomous trading'}
                            </div>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          id="sentinelEnabled"
                          checked={form.sentinelMode?.enabled || false}
                          onChange={(e) => setForm((prev) => ({
                            ...prev,
                            sentinelMode: { ...prev.sentinelMode, enabled: e.target.checked }
                          }))}
                          className="sr-only"
                        />
                      </label>
                    </div>
                    
                    {/* Settings Panel - Only show when enabled */}
                    {form.sentinelMode?.enabled && (
                      <div className="space-y-4 p-5 rounded-xl bg-slate-900/50 border border-cyan-500/30">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                            <h4 className="text-cyan-300 font-bold">Sentinel Configuration</h4>
                          </div>
                          
                          {/* Paper/Real Mode Toggle */}
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => setForm((prev) => ({
                                ...prev,
                                sentinelMode: { ...prev.sentinelMode, mode: 'paper' }
                              }))}
                              className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
                                form.sentinelMode?.mode !== 'real'
                                  ? 'bg-amber-500/30 border-2 border-amber-400 text-amber-200'
                                  : 'bg-slate-800 border border-slate-600 text-slate-400 hover:border-amber-500/50'
                              }`}
                            >
                              📝 Paper
                            </button>
                            <button
                              type="button"
                              onClick={() => setForm((prev) => ({
                                ...prev,
                                sentinelMode: { ...prev.sentinelMode, mode: 'real' }
                              }))}
                              className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
                                form.sentinelMode?.mode === 'real'
                                  ? 'bg-green-500/30 border-2 border-green-400 text-green-200'
                                  : 'bg-slate-800 border border-slate-600 text-slate-400 hover:border-green-500/50'
                              }`}
                            >
                              💰 Real
                            </button>
                          </div>
                        </div>
                        
                        {form.sentinelMode?.mode === 'real' && (
                          <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 mb-4">
                            <p className="text-green-300 text-sm font-medium">
                              ⚠️ <strong>REAL MODE:</strong> Sentinel will execute actual trades with real USDC. Ensure your wallet is funded.
                            </p>
                          </div>
                        )}
                        
                        {((form.sentinelMode?.maxPortfolioRisk ?? 15) > 50 || (form.sentinelMode?.maxSingleTradeRisk ?? 3) > 25) && (
                          <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/50 mb-4">
                            <p className="text-red-300 text-sm font-medium">
                              🔥 <strong>HIGH RISK WARNING:</strong> You have set aggressive risk limits. 
                              {(form.sentinelMode?.maxPortfolioRisk ?? 15) > 50 && ` Portfolio risk at ${form.sentinelMode?.maxPortfolioRisk}% means most of your balance can be at risk.`}
                              {(form.sentinelMode?.maxSingleTradeRisk ?? 3) > 25 && ` Trade risk at ${form.sentinelMode?.maxSingleTradeRisk}% means large single bets.`}
                              {' '}Your money, your choice - but losses can be significant.
                            </p>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <NumberField
                            label="Max Portfolio Risk %"
                            value={form.sentinelMode?.maxPortfolioRisk ?? 15}
                            onChange={(v) => setForm((prev) => ({
                              ...prev,
                              sentinelMode: { ...prev.sentinelMode, maxPortfolioRisk: Math.max(1, Math.min(100, Number(v))) }
                            }))}
                            min={1}
                            max={100}
                            step={1}
                            hint="Maximum % of balance at risk across all positions (up to 100%)"
                          />
                          <NumberField
                            label="Max Single Trade Risk %"
                            value={form.sentinelMode?.maxSingleTradeRisk ?? 3}
                            onChange={(v) => setForm((prev) => ({
                              ...prev,
                              sentinelMode: { ...prev.sentinelMode, maxSingleTradeRisk: Math.max(1, Math.min(100, Number(v))) }
                            }))}
                            min={1}
                            max={100}
                            step={1}
                            hint="Maximum % of balance risked per trade (up to 100%)"
                          />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <NumberField
                            label="Kelly Fraction"
                            value={form.sentinelMode?.kellyFraction ?? 0.25}
                            onChange={(v) => setForm((prev) => ({
                              ...prev,
                              sentinelMode: { ...prev.sentinelMode, kellyFraction: Math.max(0.1, Math.min(1, Number(v))) }
                            }))}
                            min={0.1}
                            max={1}
                            step={0.05}
                            hint="0.25 = Quarter Kelly (safer), 1.0 = Full Kelly (aggressive)"
                          />
                          <NumberField
                            label="Min Edge Required %"
                            value={Math.round((form.sentinelMode?.minEdgeRequired ?? 0) * 100)}
                            onChange={(v) => setForm((prev) => ({
                              ...prev,
                              sentinelMode: { ...prev.sentinelMode, minEdgeRequired: Math.max(0, Math.min(100, Number(v) || 0)) / 100 }
                            }))}
                            min={0}
                            max={100}
                            step={1}
                            hint="Minimum expected edge to enter a trade. 0 = disabled. Higher = fewer but higher-quality entries."
                          />
                        </div>

                        <div className="mt-4 p-4 rounded-lg bg-slate-800/40 border border-slate-700">
                          <p className="text-slate-200 font-semibold text-sm">Trade Sizing</p>
                          <p className="text-slate-400/70 text-xs mt-1">Default is Kelly. You can override with a fixed USD amount (min $1) or fixed shares (min 5).</p>

                          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="label">Sizing Mode</label>
                              <select
                                value={form.sentinelMode?.tradeSizing?.mode || 'kelly'}
                                onChange={(e) => {
                                  const mode = e.target.value;
                                  setForm((prev) => ({
                                    ...prev,
                                    sentinelMode: {
                                      ...prev.sentinelMode,
                                      tradeSizing: { ...(prev.sentinelMode?.tradeSizing || {}), mode }
                                    }
                                  }));
                                }}
                                className="input"
                              >
                                <option value="kelly">Kelly (default)</option>
                                <option value="usd">Fixed USD</option>
                                <option value="shares">Fixed Shares</option>
                              </select>
                            </div>

                            <div className={form.sentinelMode?.tradeSizing?.mode === 'usd' ? '' : 'opacity-40'}>
                              <NumberField
                                label="USD Amount"
                                value={form.sentinelMode?.tradeSizing?.usdAmount ?? 1}
                                onChange={(v) => setForm((prev) => ({
                                  ...prev,
                                  sentinelMode: {
                                    ...prev.sentinelMode,
                                    tradeSizing: {
                                      ...(prev.sentinelMode?.tradeSizing || {}),
                                      usdAmount: Math.max(1, Number(v) || 1)
                                    }
                                  }
                                }))}
                                min={1}
                                max={1000000}
                                step={0.5}
                                hint="Min $1"
                              />
                            </div>

                            <div className={form.sentinelMode?.tradeSizing?.mode === 'shares' ? '' : 'opacity-40'}>
                              <NumberField
                                label="Share Amount"
                                value={form.sentinelMode?.tradeSizing?.shareAmount ?? 8}
                                onChange={(v) => setForm((prev) => ({
                                  ...prev,
                                  sentinelMode: {
                                    ...prev.sentinelMode,
                                    tradeSizing: {
                                      ...(prev.sentinelMode?.tradeSizing || {}),
                                      shareAmount: Math.max(8, Number(v) || 8)
                                    }
                                  }
                                }))}
                                min={8}
                                max={1000000000}
                                step={1}
                                hint="Min 8 shares (FAK partial fills stay above 5 min)"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Order Type Selection */}
                        <div className="mt-4 p-4 rounded-lg bg-slate-800/40 border border-slate-700">
                          <p className="text-slate-200 font-semibold text-sm">Order Execution Type</p>
                          <p className="text-slate-400/70 text-xs mt-1">
                            How buy orders are executed. GTC sits in orderbook until filled. FOK requires full fill or cancels. FAK allows partial fills.
                          </p>
                          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="label">Order Type</label>
                              <select
                                value={form.sentinelMode?.orderType || 'GTC'}
                                onChange={(e) => {
                                  setForm((prev) => ({
                                    ...prev,
                                    sentinelMode: {
                                      ...prev.sentinelMode,
                                      orderType: e.target.value
                                    }
                                  }));
                                }}
                                className="input"
                              >
                                <option value="GTC">GTC (Good-Til-Cancelled)</option>
                                <option value="FOK">FOK (Fill-Or-Kill)</option>
                                <option value="FAK">FAK (Fill-And-Kill)</option>
                              </select>
                            </div>
                            <div className="md:col-span-2 flex items-end">
                              <div className="text-xs text-slate-400/80 p-2 rounded bg-slate-900/50">
                                {form.sentinelMode?.orderType === 'GTC' && (
                                  <span>📋 <strong>GTC:</strong> Order sits in orderbook until fully filled. Best for getting exact price, but may take time.</span>
                                )}
                                {form.sentinelMode?.orderType === 'FOK' && (
                                  <span>⚡ <strong>FOK:</strong> Must fill entire order immediately or it cancels. Fast but may miss trades if liquidity is low.</span>
                                )}
                                {form.sentinelMode?.orderType === 'FAK' && (
                                  <span>🔄 <strong>FAK:</strong> Fills what it can immediately, cancels the rest. Good balance of speed and fill rate. Min 8 shares ensures partial fills stay above 5 share minimum.</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Paper Mode Virtual Bankroll */}
                        {form.sentinelMode?.mode !== 'real' && (
                          <div className="mt-4">
                            <NumberField
                              label="Paper Mode Bankroll (USDC)"
                              value={form.sentinelMode?.paperBankroll ?? 10000}
                              onChange={(v) => setForm((prev) => ({
                                ...prev,
                                sentinelMode: { ...prev.sentinelMode, paperBankroll: Math.max(1, Number(v)) }
                              }))}
                              min={1}
                              max={1000000}
                              step={1}
                              hint="Virtual bankroll for paper trading. This is your simulated total capital."
                            />
                          </div>
                        )}
                        
                        {/* Override Risk Blocks Toggle */}
                        <div className="mt-4 p-4 rounded-lg bg-amber-950/30 border border-amber-500/30">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-amber-300 font-semibold">⚡ Aggressive Mode (Override Risk Blocks)</p>
                              <p className="text-amber-400/70 text-xs mt-1">
                                Bypass portfolio risk limits. Sentinel will trade even when risk thresholds are exceeded. Use with caution!
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={form.sentinelMode?.overrideRiskBlocks || false}
                                onChange={(e) => setForm((prev) => ({
                                  ...prev,
                                  sentinelMode: { ...prev.sentinelMode, overrideRiskBlocks: e.target.checked }
                                }))}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                            </label>
                          </div>
                          {form.sentinelMode?.overrideRiskBlocks && (
                            <div className="mt-3 p-2 rounded bg-red-500/20 border border-red-500/40">
                              <p className="text-red-300 text-xs font-medium">
                                ⚠️ WARNING: Risk limits disabled. You may lose more than expected. Monitor closely!
                              </p>
                            </div>
                          )}
                        </div>

                        {/* MASTER EXIT OVERRIDES */}
                        <div className="mt-4 p-4 rounded-lg bg-purple-950/40 border border-purple-500/40">
                          <p className="text-purple-300 font-semibold mb-3">🔒 Master Exit Overrides</p>
                          <p className="text-purple-400/70 text-xs mb-4">
                            These settings OVERRIDE all other stop losses and sell triggers. Use for diamond-hand strategies.
                          </p>
                          
                          {/* No Stop Losses Toggle */}
                          <div className="flex items-center justify-between mb-4 p-3 rounded-lg bg-slate-800/50">
                            <div>
                              <p className="text-purple-200 font-medium">💎 No Stop Losses</p>
                              <p className="text-purple-300/60 text-xs mt-1">
                                Disable ALL stop losses and sell triggers. Only sell via "Sell Only Over 90¢" if enabled.
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={form.sentinelMode?.noStopLosses || false}
                                onChange={(e) => setForm((prev) => ({
                                  ...prev,
                                  sentinelMode: { ...prev.sentinelMode, noStopLosses: e.target.checked }
                                }))}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                            </label>
                          </div>

                          {/* Sell Only Over 90 Cents Toggle */}
                          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                            <div>
                              <p className="text-purple-200 font-medium">📈 Sell Only Over 90¢</p>
                              <p className="text-purple-300/60 text-xs mt-1">
                                Only sell when price ≥ $0.90. If "No Stop Losses" is OFF, includes 80% emergency stop.
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={form.sentinelMode?.sellOnlyOver90Cents || false}
                                onChange={(e) => setForm((prev) => ({
                                  ...prev,
                                  sentinelMode: { ...prev.sentinelMode, sellOnlyOver90Cents: e.target.checked }
                                }))}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                            </label>
                          </div>

                          {/* Market Type Filter - Only show when No SL or 90¢ mode is enabled */}
                          {(form.sentinelMode?.noStopLosses || form.sentinelMode?.sellOnlyOver90Cents) && (
                            <div className="mt-4 p-3 rounded-lg bg-slate-800/50">
                              <p className="text-purple-200 font-medium mb-2">🎯 Apply To Which Markets?</p>
                              <p className="text-purple-300/60 text-xs mb-3">
                                Choose which market types the No SL / 90¢ rules apply to. Other markets will use normal exit rules.
                              </p>
                              <select
                                value={form.sentinelMode?.noStopLossesMarketFilter || 'all'}
                                onChange={(e) => setForm((prev) => ({
                                  ...prev,
                                  sentinelMode: { ...prev.sentinelMode, noStopLossesMarketFilter: e.target.value }
                                }))}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-purple-100 focus:outline-none focus:border-purple-500"
                              >
                                <option value="all">🌐 All Markets</option>
                                <option value="crypto">₿ Crypto Only (BTC/ETH/SOL up/down)</option>
                                <option value="sports">🏈 Sports Only (NFL, NBA, etc.)</option>
                                <option value="politics">🏛️ Politics Only (Elections, Trump, etc.)</option>
                                <option value="other">📦 Other Markets Only</option>
                              </select>
                              {form.sentinelMode?.noStopLossesMarketFilter && form.sentinelMode?.noStopLossesMarketFilter !== 'all' && (
                                <p className="mt-2 text-xs text-yellow-400/80">
                                  ⚠️ Only <strong>{form.sentinelMode?.noStopLossesMarketFilter}</strong> markets will use No SL / 90¢ rules. Other markets use normal exits.
                                </p>
                              )}
                            </div>
                          )}

                          {/* Loss Mitigation Controls - Only show when No SL or 90¢ mode is enabled */}
                          {(form.sentinelMode?.noStopLosses || form.sentinelMode?.sellOnlyOver90Cents) && (
                            <div className="mt-4 p-3 rounded-lg bg-green-900/30 border border-green-500/30">
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <p className="text-green-200 font-medium">🛡️ Prevent Total Wipeouts</p>
                                  <p className="text-green-300/60 text-xs">Still holds through volatility, but won't let you lose everything</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={form.sentinelMode?.lossMitigationEnabled !== false}
                                    onChange={(e) => setForm((prev) => ({
                                      ...prev,
                                      sentinelMode: { ...prev.sentinelMode, lossMitigationEnabled: e.target.checked }
                                    }))}
                                    className="sr-only peer"
                                  />
                                  <div className="w-9 h-5 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                                </label>
                              </div>
                              
                              {form.sentinelMode?.lossMitigationEnabled === false && (
                                <div className="p-2 bg-red-900/50 border border-red-500/50 rounded mt-2">
                                  <p className="text-red-200 text-xs font-bold">⚠️ WARNING: Positions can drop to -100% (total loss)</p>
                                </div>
                              )}
                              
                              {form.sentinelMode?.lossMitigationEnabled !== false && (
                              <>
                              {/* Catastrophic Loss Floor */}
                              <div className="mb-3 mt-3 p-2 bg-slate-800/50 rounded">
                                <label className="text-green-200 text-sm font-medium">🚨 Max Loss Before Force-Sell</label>
                                <p className="text-green-300/50 text-xs mb-2">If a position drops this much, sell EVERYTHING to stop the bleeding</p>
                                <div className="flex items-center gap-2">
                                  <span className="text-red-400 text-lg font-bold">-</span>
                                  <input
                                    type="number"
                                    value={Math.abs(form.sentinelMode?.catastrophicLossFloor ?? 70)}
                                    onChange={(e) => setForm((prev) => ({
                                      ...prev,
                                      sentinelMode: { ...prev.sentinelMode, catastrophicLossFloor: -Math.abs(Number(e.target.value)) }
                                    }))}
                                    className="w-16 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-green-100 text-sm"
                                    min="30"
                                    max="99"
                                  />
                                  <span className="text-green-300 text-sm font-medium">% loss = Force sell</span>
                                </div>
                                <p className="text-red-400/70 text-xs mt-1">Example: 70 means sell if position drops 70%</p>
                              </div>
                              
                              {/* Half-Sell Threshold */}
                              <div className="mb-3 p-2 bg-slate-800/50 rounded border border-yellow-500/20">
                                <label className="text-yellow-200 text-sm font-medium">✂️ Partial Exit: Sell HALF at This Loss</label>
                                <p className="text-green-300/50 text-xs mb-2">Sells only 50% of your shares. Keeps the other half in case it recovers. Does NOT flip to other side.</p>
                                <div className="flex items-center gap-2">
                                  <span className="text-red-400 text-lg font-bold">-</span>
                                  <input
                                    type="number"
                                    value={Math.abs(form.sentinelMode?.halfSellAtLoss ?? 50)}
                                    onChange={(e) => setForm((prev) => ({
                                      ...prev,
                                      sentinelMode: { ...prev.sentinelMode, halfSellAtLoss: -Math.abs(Number(e.target.value)) }
                                    }))}
                                    className="w-16 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-green-100 text-sm"
                                    min="0"
                                    max="80"
                                  />
                                  <span className="text-green-300 text-sm font-medium">% loss = Sell half</span>
                                </div>
                                <p className="text-yellow-400/70 text-xs mt-1">Set to 0 to DISABLE half-sell. Example: 50 means sell half if position drops 50%</p>
                                {Math.abs(form.sentinelMode?.halfSellAtLoss ?? 50) === 0 && (
                                  <p className="text-blue-400 text-xs mt-1 font-bold">✅ Half-sell is DISABLED</p>
                                )}
                                {(form.sentinelMode?.tradeSizing?.shareAmount ?? 10) < 10 && Math.abs(form.sentinelMode?.halfSellAtLoss ?? 50) > 0 && (
                                  <p className="text-orange-400 text-xs mt-1">⚠️ Warning: With {form.sentinelMode?.tradeSizing?.shareAmount ?? 8} shares, half-sell would leave {Math.floor((form.sentinelMode?.tradeSizing?.shareAmount ?? 8) / 2)} shares (min 5 required for trades)</p>
                                )}
                              </div>
                              
                              {/* Hedge After Half-Sell Toggle */}
                              <div className="flex items-center justify-between mb-3 p-2 bg-slate-800/50 rounded border border-yellow-500/20">
                                <div>
                                  <span className="text-yellow-200 text-sm font-medium">✂️🔄 After Half-Sell: Bet Other Side Too?</span>
                                  <p className="text-green-300/50 text-xs">Uses the money from the sold half to bet on the opposite outcome. You'd then have both sides.</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={form.sentinelMode?.hedgeAfterHalfSell || false}
                                    onChange={(e) => setForm((prev) => ({
                                      ...prev,
                                      sentinelMode: { ...prev.sentinelMode, hedgeAfterHalfSell: e.target.checked }
                                    }))}
                                    className="sr-only peer"
                                  />
                                  <div className="w-9 h-5 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                                </label>
                              </div>
                              
                              {/* Hedge & Switch — DIFFERENT from scissors above */}
                              <div className="mb-3 p-2 bg-slate-800/50 rounded border border-cyan-500/30">
                                <div className="flex items-center justify-between mb-2">
                                  <div>
                                    <span className="text-cyan-200 text-sm font-medium">🔄 Hedge & Switch: Sell ALL + Flip to Other Side</span>
                                    <p className="text-cyan-300/60 text-xs">When a position is sold at a loss, automatically bet the OPPOSITE outcome. Different from ✂️ which only sells half.</p>
                                  </div>
                                  <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={form.sentinelMode?.hedgeEnabled || false}
                                      onChange={(e) => setForm((prev) => ({
                                        ...prev,
                                        sentinelMode: { ...prev.sentinelMode, hedgeEnabled: e.target.checked }
                                      }))}
                                      className="sr-only peer"
                                    />
                                    <div className="w-9 h-5 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-cyan-500"></div>
                                  </label>
                                </div>
                                {form.sentinelMode?.hedgeEnabled && (
                                  <>
                                    <div className="flex items-center gap-2 mt-2">
                                      <span className="text-cyan-300 text-xs">Min loss to flip:</span>
                                      <span className="text-red-400 text-sm font-bold">-</span>
                                      <input
                                        type="number"
                                        value={Math.abs(form.sentinelMode?.hedgeTriggerPercent ?? 25)}
                                        onChange={(e) => setForm((prev) => ({
                                          ...prev,
                                          sentinelMode: { ...prev.sentinelMode, hedgeTriggerPercent: -Math.abs(Number(e.target.value)) }
                                        }))}
                                        className="w-14 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-green-100 text-sm"
                                        min="5"
                                        max="80"
                                      />
                                      <span className="text-cyan-300 text-xs">% loss</span>
                                    </div>
                                    <p className="text-yellow-400/70 text-xs mt-1">When ANY stop loss fires at or beyond this loss %, the bot will sell everything and buy the opposite outcome. Also overrides sell blocks (no SL, sell above 90¢).</p>
                                  </>
                                )}
                                {!form.sentinelMode?.hedgeEnabled && (
                                  <p className="text-slate-500 text-xs mt-1">OFF — positions will just sell at stop loss without flipping to other side.</p>
                                )}
                              </div>
                              
                              {/* Confidence-Based Sizing Toggle */}
                              <div className="flex items-center justify-between p-2 bg-slate-800/50 rounded">
                                <div>
                                  <span className="text-green-200 text-sm font-medium">📉 Bet Less on Risky Entries</span>
                                  <p className="text-green-300/50 text-xs">Lower price = less confident = smaller bet. Higher price = more confident = bigger bet.</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={form.sentinelMode?.confidenceBasedSizing !== false}
                                    onChange={(e) => setForm((prev) => ({
                                      ...prev,
                                      sentinelMode: { ...prev.sentinelMode, confidenceBasedSizing: e.target.checked }
                                    }))}
                                    className="sr-only peer"
                                  />
                                  <div className="w-9 h-5 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                                </label>
                              </div>
                              
                              {form.sentinelMode?.confidenceBasedSizing !== false && (
                                <p className="mt-2 text-xs text-green-400/70 p-2 bg-slate-800/30 rounded">
                                  Example: 85¢+ entry = full bet | 75¢ entry = 85% bet | 65¢ entry = 70% bet | Under 65¢ = half bet
                                </p>
                              )}
                              </>
                              )}
                            </div>
                          )}

                          {/* Active Mode Summary */}
                          {(form.sentinelMode?.noStopLosses || form.sentinelMode?.sellOnlyOver90Cents) && (
                            <div className={`mt-3 p-3 rounded-lg border ${form.sentinelMode?.noStopLosses && form.sentinelMode?.sellOnlyOver90Cents ? 'bg-purple-500/20 border-purple-500/50' : form.sentinelMode?.noStopLosses ? 'bg-red-500/20 border-red-500/50' : 'bg-blue-500/20 border-blue-500/50'}`}>
                              <p className={`text-sm font-medium ${form.sentinelMode?.noStopLosses && form.sentinelMode?.sellOnlyOver90Cents ? 'text-purple-200' : form.sentinelMode?.noStopLosses ? 'text-red-200' : 'text-blue-200'}`}>
                                {form.sentinelMode?.noStopLosses && form.sentinelMode?.sellOnlyOver90Cents && (
                                  <>🔮 <strong>DIAMOND HANDS:</strong> Will ONLY sell when price ≥ 90¢. No exceptions.</>
                                )}
                                {form.sentinelMode?.noStopLosses && !form.sentinelMode?.sellOnlyOver90Cents && (
                                  <>💀 <strong>HOLD FOREVER:</strong> NO sells for ANY reason. Position will never auto-close.</>
                                )}
                                {!form.sentinelMode?.noStopLosses && form.sentinelMode?.sellOnlyOver90Cents && (
                                  <>📊 <strong>90¢ TARGET:</strong> Sells only at ≥90¢ OR at -80% emergency stop.</>
                                )}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {/* Per-Market-Type Share Limits */}
                        <div className="mt-4 p-4 rounded-lg bg-amber-950/30 border border-amber-700/30">
                          <p className="text-amber-200 font-semibold mb-1">🎯 Which Markets Should I Trade?</p>
                          <p className="text-amber-300/60 text-xs mb-3">Set max shares per market type. <strong className="text-amber-200">Set to 0 = NEVER trade that type.</strong></p>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div className="p-2 bg-slate-800/50 rounded">
                              <label className="text-amber-200 text-sm font-medium block mb-1">₿ Crypto (BTC, ETH, SOL up/down)</label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  value={form.sentinelMode?.marketTypeShares?.crypto ?? 10}
                                  onChange={(e) => setForm((prev) => ({
                                    ...prev,
                                    sentinelMode: { 
                                      ...prev.sentinelMode, 
                                      marketTypeShares: { ...prev.sentinelMode?.marketTypeShares, crypto: Number(e.target.value) }
                                    }
                                  }))}
                                  className="w-16 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-amber-100 text-sm"
                                  min="0"
                                  max="100"
                                />
                                <span className="text-amber-300/70 text-xs">shares max</span>
                              </div>
                              {form.sentinelMode?.marketTypeShares?.crypto === 0 && (
                                <p className="text-red-400 text-xs mt-1 font-bold">🚫 BLOCKED - won't trade crypto</p>
                              )}
                            </div>
                            
                            <div className="p-2 bg-slate-800/50 rounded">
                              <label className="text-amber-200 text-sm font-medium block mb-1">🏈 Sports (NFL, NBA, etc.)</label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  value={form.sentinelMode?.marketTypeShares?.sports ?? 10}
                                  onChange={(e) => setForm((prev) => ({
                                    ...prev,
                                    sentinelMode: { 
                                      ...prev.sentinelMode, 
                                      marketTypeShares: { ...prev.sentinelMode?.marketTypeShares, sports: Number(e.target.value) }
                                    }
                                  }))}
                                  className="w-16 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-amber-100 text-sm"
                                  min="0"
                                  max="100"
                                />
                                <span className="text-amber-300/70 text-xs">shares max</span>
                              </div>
                              {form.sentinelMode?.marketTypeShares?.sports === 0 && (
                                <p className="text-red-400 text-xs mt-1 font-bold">🚫 BLOCKED - won't trade sports</p>
                              )}
                            </div>
                            
                            <div className="p-2 bg-slate-800/50 rounded">
                              <label className="text-amber-200 text-sm font-medium block mb-1">🏛️ Politics (Elections, etc.)</label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  value={form.sentinelMode?.marketTypeShares?.politics ?? 10}
                                  onChange={(e) => setForm((prev) => ({
                                    ...prev,
                                    sentinelMode: { 
                                      ...prev.sentinelMode, 
                                      marketTypeShares: { ...prev.sentinelMode?.marketTypeShares, politics: Number(e.target.value) }
                                    }
                                  }))}
                                  className="w-16 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-amber-100 text-sm"
                                  min="0"
                                  max="100"
                                />
                                <span className="text-amber-300/70 text-xs">shares max</span>
                              </div>
                              {form.sentinelMode?.marketTypeShares?.politics === 0 && (
                                <p className="text-red-400 text-xs mt-1 font-bold">🚫 BLOCKED - won't trade politics</p>
                              )}
                            </div>
                            
                            <div className="p-2 bg-slate-800/50 rounded">
                              <label className="text-amber-200 text-sm font-medium block mb-1">📦 Everything Else</label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  value={form.sentinelMode?.marketTypeShares?.other ?? 10}
                                  onChange={(e) => setForm((prev) => ({
                                    ...prev,
                                    sentinelMode: { 
                                      ...prev.sentinelMode, 
                                      marketTypeShares: { ...prev.sentinelMode?.marketTypeShares, other: Number(e.target.value) }
                                    }
                                  }))}
                                  className="w-16 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-amber-100 text-sm"
                                  min="0"
                                  max="100"
                                />
                                <span className="text-amber-300/70 text-xs">shares max</span>
                              </div>
                              {form.sentinelMode?.marketTypeShares?.other === 0 && (
                                <p className="text-red-400 text-xs mt-1 font-bold">🚫 BLOCKED - won't trade other</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Sentinel Stop Loss by Market Type */}
                        <div className="mt-4 p-4 rounded-lg bg-red-950/30 border border-red-700/30">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="text-red-200 font-semibold">🛑 Stop Loss by Market Type</p>
                              <p className="text-red-300/60 text-xs">Optional. If OFF, Sentinel uses its normal stop loss logic. If ON, Sentinel will use your % per market type.</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={form.sentinelMode?.perMarketStopLossEnabled || false}
                                onChange={(e) => setForm((prev) => ({
                                  ...prev,
                                  sentinelMode: { ...prev.sentinelMode, perMarketStopLossEnabled: e.target.checked }
                                }))}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                            </label>
                          </div>

                          {form.sentinelMode?.perMarketStopLossEnabled && (
                            <>
                              <p className="text-red-200/80 text-xs mb-3">Example: <strong>Sports 30</strong> = sell if down 30%. <strong>Crypto 80</strong> = sell if down 80%.</p>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="p-2 bg-slate-800/50 rounded">
                                  <label className="text-red-200 text-sm font-medium block mb-1">🏈 Sports Stop Loss</label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="number"
                                      value={form.sentinelMode?.stopLossByMarketType?.sports ?? 30}
                                      onChange={(e) => setForm((prev) => ({
                                        ...prev,
                                        sentinelMode: {
                                          ...prev.sentinelMode,
                                          stopLossByMarketType: { ...prev.sentinelMode?.stopLossByMarketType, sports: Number(e.target.value) }
                                        }
                                      }))}
                                      className="w-16 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-red-100 text-sm"
                                      min="1"
                                      max="99"
                                    />
                                    <span className="text-red-300/70 text-xs">% loss</span>
                                  </div>
                                </div>

                                <div className="p-2 bg-slate-800/50 rounded">
                                  <label className="text-red-200 text-sm font-medium block mb-1">₿ Crypto Stop Loss</label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="number"
                                      value={form.sentinelMode?.stopLossByMarketType?.crypto ?? 80}
                                      onChange={(e) => setForm((prev) => ({
                                        ...prev,
                                        sentinelMode: {
                                          ...prev.sentinelMode,
                                          stopLossByMarketType: { ...prev.sentinelMode?.stopLossByMarketType, crypto: Number(e.target.value) }
                                        }
                                      }))}
                                      className="w-16 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-red-100 text-sm"
                                      min="1"
                                      max="99"
                                    />
                                    <span className="text-red-300/70 text-xs">% loss</span>
                                  </div>
                                </div>

                                <div className="p-2 bg-slate-800/50 rounded">
                                  <label className="text-red-200 text-sm font-medium block mb-1">🏛️ Politics Stop Loss</label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="number"
                                      value={form.sentinelMode?.stopLossByMarketType?.politics ?? 40}
                                      onChange={(e) => setForm((prev) => ({
                                        ...prev,
                                        sentinelMode: {
                                          ...prev.sentinelMode,
                                          stopLossByMarketType: { ...prev.sentinelMode?.stopLossByMarketType, politics: Number(e.target.value) }
                                        }
                                      }))}
                                      className="w-16 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-red-100 text-sm"
                                      min="1"
                                      max="99"
                                    />
                                    <span className="text-red-300/70 text-xs">% loss</span>
                                  </div>
                                </div>

                                <div className="p-2 bg-slate-800/50 rounded">
                                  <label className="text-red-200 text-sm font-medium block mb-1">📦 Other Stop Loss</label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="number"
                                      value={form.sentinelMode?.stopLossByMarketType?.other ?? 40}
                                      onChange={(e) => setForm((prev) => ({
                                        ...prev,
                                        sentinelMode: {
                                          ...prev.sentinelMode,
                                          stopLossByMarketType: { ...prev.sentinelMode?.stopLossByMarketType, other: Number(e.target.value) }
                                        }
                                      }))}
                                      className="w-16 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-red-100 text-sm"
                                      min="1"
                                      max="99"
                                    />
                                    <span className="text-red-300/70 text-xs">% loss</span>
                                  </div>
                                </div>
                              </div>

                              <div className="mt-3 p-2 bg-slate-900/60 border border-red-700/30 rounded">
                                <p className="text-red-200 text-xs font-bold">⚠️ Higher % = BIGGER allowed loss.</p>
                                <p className="text-red-300/70 text-xs">Crypto at 80% means you can lose 80% before auto-sell triggers.</p>
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* What Sentinel Controls */}
                        <div className="mt-4 p-4 rounded-lg bg-cyan-950/50 border border-cyan-700/30">
                          <p className="text-cyan-300 font-semibold mb-2">🎛️ Sentinel Controls:</p>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex items-center gap-2 text-cyan-400/80">
                              <CheckCircle2 className="w-3 h-3" /> Entry Signal Analysis
                            </div>
                            <div className="flex items-center gap-2 text-cyan-400/80">
                              <CheckCircle2 className="w-3 h-3" /> Position Sizing (Kelly)
                            </div>
                            <div className="flex items-center gap-2 text-cyan-400/80">
                              <CheckCircle2 className="w-3 h-3" /> Dynamic Stop Losses
                            </div>
                            <div className="flex items-center gap-2 text-cyan-400/80">
                              <CheckCircle2 className="w-3 h-3" /> Take Profit Levels
                            </div>
                            <div className="flex items-center gap-2 text-cyan-400/80">
                              <CheckCircle2 className="w-3 h-3" /> Balance Management
                            </div>
                            <div className="flex items-center gap-2 text-cyan-400/80">
                              <CheckCircle2 className="w-3 h-3" /> Recovery Mode
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Sentinel PNL Stats Panel - Always show when Sentinel enabled */}
                {form.sentinelMode?.enabled && (
                  <div className="p-4 rounded-xl bg-slate-900/80 border border-cyan-500/30">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-cyan-400" />
                        <h4 className="text-cyan-300 font-bold text-sm">Sentinel Performance</h4>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={resetSentinelStats}
                          disabled={isSentinelStatsResetting}
                          className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50"
                          title="Reset Sentinel stats"
                        >
                          {isSentinelStatsResetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                        </button>
                        <button 
                          onClick={fetchSentinelStats}
                          disabled={isSentinelStatsLoading}
                          className="text-xs text-dark-500 hover:text-dark-300"
                          title="Refresh Sentinel stats"
                        >
                          {isSentinelStatsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        </button>
                      </div>
                    </div>
                    {isSentinelStatsLoading && !sentinelStats && (
                      <div className="text-center text-dark-500 text-xs py-4">
                        Loading Sentinel performance data...
                      </div>
                    )}
                    {!isSentinelStatsLoading && !sentinelStats && (
                      <div className="text-center text-dark-500 text-xs py-4">
                        No Sentinel trades yet. Stats will appear once Sentinel starts trading.
                      </div>
                    )}
                    {sentinelStats && (
                      <>
                        {sentinelStats.statsResetAt && (
                          <div className="text-xs text-dark-500 mb-2">
                            Stats since: {new Date(sentinelStats.statsResetAt).toLocaleString()}
                          </div>
                        )}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                            <div className={`text-lg font-bold ${(sentinelStats.totalPnL || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {(sentinelStats.totalPnL || 0) >= 0 ? '+' : ''}${sentinelStats.totalPnL?.toFixed(2) || '0.00'}
                            </div>
                            <div className="text-xs text-dark-400">Total PnL</div>
                          </div>
                          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                            <div className={`text-lg font-bold ${(sentinelStats.totalRealizedPnL || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {(sentinelStats.totalRealizedPnL || 0) >= 0 ? '+' : ''}${sentinelStats.totalRealizedPnL?.toFixed(2) || '0.00'}
                            </div>
                            <div className="text-xs text-dark-400">Realized</div>
                          </div>
                          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                            <div className={`text-lg font-bold ${(sentinelStats.totalUnrealizedPnL || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {(sentinelStats.totalUnrealizedPnL || 0) >= 0 ? '+' : ''}${sentinelStats.totalUnrealizedPnL?.toFixed(2) || '0.00'}
                            </div>
                            <div className="text-xs text-dark-400">Unrealized</div>
                          </div>
                          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                            <div className="text-lg font-bold text-cyan-400">
                              {sentinelStats.winRate?.toFixed(1) || '0'}%
                            </div>
                            <div className="text-xs text-dark-400">Win Rate</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-2 mt-3 text-center text-xs">
                          <div>
                            <span className="text-green-400 font-bold">{sentinelStats.wins || 0}</span>
                            <span className="text-dark-500 ml-1">Wins</span>
                          </div>
                          <div>
                            <span className="text-red-400 font-bold">{sentinelStats.losses || 0}</span>
                            <span className="text-dark-500 ml-1">Losses</span>
                          </div>
                          <div>
                            <span className="text-cyan-400 font-bold">{sentinelStats.openPositions || 0}</span>
                            <span className="text-dark-500 ml-1">Open</span>
                          </div>
                          <div>
                            <span className="text-dark-400 font-bold">{sentinelStats.closedPositions || 0}</span>
                            <span className="text-dark-500 ml-1">Closed</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                
                {/* Overlay message when Sentinel is active - show on other settings */}
                {form.sentinelMode?.enabled && (
                  <div className="mt-4 p-4 rounded-xl bg-cyan-950/50 border border-cyan-500/30 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                      <span className="text-xl">🤖</span>
                    </div>
                    <div>
                      <p className="text-cyan-300 font-semibold">Sentinel Mode Active</p>
                      <p className="text-cyan-400/70 text-sm">
                        Manual stop loss, take profit, and trade amount settings below are <strong>overridden</strong> by Sentinel's AI algorithms.
                      </p>
                    </div>
                  </div>
                )}

                {/* Discord Webhook */}
                <div className="p-4 rounded-xl bg-dark-800/60 border border-dark-700">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xl">🔔</span>
                    <h3 className="font-semibold text-white">Discord Notifications</h3>
                  </div>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <input
                      type="checkbox"
                      id="discordEnabled"
                      checked={form.discord?.enabled || false}
                      onChange={(e) => setForm((prev) => ({
                        ...prev,
                        discord: { ...prev.discord, enabled: e.target.checked }
                      }))}
                      className="w-4 h-4 rounded border-dark-600 bg-dark-800 text-primary-500 focus:ring-primary-500"
                    />
                    <label htmlFor="discordEnabled" className="text-sm font-medium">Enable Discord Webhook</label>
                  </div>
                  
                  {form.discord?.enabled && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-300">Webhook URL</label>
                      <input
                        type="text"
                        value={form.discord?.webhookUrl || ''}
                        onChange={(e) => setForm((prev) => ({
                          ...prev,
                          discord: { ...prev.discord, webhookUrl: e.target.value }
                        }))}
                        placeholder="https://discord.com/api/webhooks/..."
                        className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-gray-500 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <p className="text-xs text-gray-500">Get this from Discord: Server Settings → Integrations → Webhooks</p>
                    </div>
                  )}
                </div>

                {/* Execution Settings */}
                <div className="p-4 rounded-xl bg-dark-800/60 border border-dark-700">
                  <h3 className="font-semibold text-white mb-4">Execution Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      {form.sentinelMode?.enabled ? (
                        <div>
                          <label className="label">Order Type</label>
                          <div className="input bg-dark-700/50 text-cyan-400 cursor-not-allowed flex items-center gap-2">
                            <span>🤖 GTC (Sentinel Autonomous)</span>
                          </div>
                          <p className="mt-1 text-xs text-cyan-500">
                            Sentinel mode always uses GTC orders for optimal tracking and execution. This cannot be changed.
                          </p>
                        </div>
                      ) : (
                        <>
                          <SelectField
                            label="Order Type"
                            value={form.execution?.orderType || 'MARKET'}
                            onChange={(v) => setForm((prev) => ({
                              ...prev,
                              execution: { ...prev.execution, orderType: v }
                            }))}
                            options={[
                              { value: 'MARKET', label: 'MARKET (aggressive, best fill rate)' },
                              { value: 'FOK', label: 'FOK (Fill-or-Kill)' },
                              { value: 'FAK', label: 'FAK (Fill-and-Kill)' },
                              { value: 'GTC', label: 'GTC (Good-til-Cancelled)' }
                            ]}
                            hint={
                              form.execution?.orderType === 'GTC' 
                                ? "Order stays in orderbook up to 30 min until filled - better prices but may not fill"
                                : form.execution?.orderType === 'FOK'
                                ? "Entire order must fill immediately or it cancels"
                                : form.execution?.orderType === 'FAK'
                                ? "Fills what it can immediately, cancels the rest"
                                : "Uses aggressive slippage for reliable instant fills"
                            }
                          />
                          {form.execution?.orderType === 'GTC' && (
                            <div className="mt-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                              <p className="text-xs text-amber-400">
                                <strong>⚠️ GTC Orders:</strong> Your order will sit in the orderbook waiting for a match. 
                                You may get better prices, but orders may take time to fill or not fill at all if the market moves away. 
                                Orders auto-cancel after 30 minutes if unfilled.
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <NumberField
                      label="Max Slippage %"
                      value={form.execution?.slippage || 5}
                      onChange={(v) => setForm((prev) => ({
                        ...prev,
                        execution: { ...prev.execution, slippage: Number(v) }
                      }))}
                      min={1}
                      max={20}
                      step={1}
                      hint="Maximum price slippage allowed"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <NumberField
                    label="Min liquidity ($)"
                    value={form.filters.minLiquidity}
                    onChange={(v) => updateNested('filters.minLiquidity', v)}
                    min={0}
                    max={1000000000}
                    step={100}
                  />
                  <NumberField
                    label="Max spread (0-0.5)"
                    value={form.filters.maxSpread}
                    onChange={(v) => updateNested('filters.maxSpread', v)}
                    min={0.001}
                    max={0.5}
                    step={0.001}
                  />
                  <NumberField
                    label="Min 24h volume ($)"
                    value={form.filters.minVolume24h}
                    onChange={(v) => updateNested('filters.minVolume24h', v)}
                    min={0}
                    max={1000000000}
                    step={100}
                  />
                </div>
                
                {/* Settings Health Check - Diagnostic panel */}
                {form.sentinelMode?.enabled && (settingsHealthCheck.warnings.length > 0 || settingsHealthCheck.activeFeatures.length > 0) && (
                  <div className="mt-6 p-4 rounded-xl bg-slate-900/80 border border-amber-500/30">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-lg">🩺</span>
                      <h4 className="text-amber-300 font-bold text-sm">Settings Health Check</h4>
                    </div>
                    
                    {/* Active Features */}
                    {settingsHealthCheck.activeFeatures.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs text-slate-400 mb-1.5 font-medium">Active Features:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {settingsHealthCheck.activeFeatures.map((f, i) => (
                            <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${f.status === 'OFF' ? 'bg-red-500/15 border border-red-500/30' : 'bg-slate-700/60 border border-slate-600/40'} ${f.color}`}>
                              {f.icon} {f.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Warnings */}
                    {settingsHealthCheck.warnings.map((w, i) => (
                      <div key={`w-${i}`} className={`mb-2 p-2.5 rounded-lg ${w.severity === 'high' ? 'bg-red-500/10 border border-red-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
                        <p className={`text-xs font-bold ${w.severity === 'high' ? 'text-red-400' : 'text-amber-400'}`}>
                          {w.severity === 'high' ? '🚨' : '⚠️'} {w.title}
                        </p>
                        <p className={`text-xs mt-0.5 ${w.severity === 'high' ? 'text-red-300/80' : 'text-amber-300/80'}`}>{w.message}</p>
                      </div>
                    ))}

                    {/* Tips */}
                    {settingsHealthCheck.tips.map((t, i) => (
                      <div key={`t-${i}`} className="mb-2 p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/30">
                        <p className="text-xs font-bold text-blue-400">💡 {t.title}</p>
                        <p className="text-xs mt-0.5 text-blue-300/80">{t.message}</p>
                      </div>
                    ))}

                    {settingsHealthCheck.warnings.length === 0 && settingsHealthCheck.tips.length === 0 && (
                      <p className="text-xs text-green-400">✅ No issues detected. Settings look balanced.</p>
                    )}
                  </div>
                )}

                {/* Sentinel Activity Log - At bottom, always show when Sentinel enabled */}
                {form.sentinelMode?.enabled && (
                  <div className="mt-6 p-4 rounded-xl bg-slate-900/80 border border-cyan-500/30">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4 text-cyan-400" />
                        <h4 className="text-cyan-300 font-bold text-sm">Sentinel Activity Log</h4>
                      </div>
                      <button 
                        onClick={clearSentinelActivity}
                        className="text-xs text-dark-500 hover:text-dark-300"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {sentinelActivity.length === 0 && (
                        <div className="text-center text-dark-500 text-xs py-4">
                          Waiting for Sentinel activity... Activity will appear here as Sentinel scans markets and makes decisions.
                        </div>
                      )}
                      {sentinelActivity.slice(0, 20).map((item, idx) => {
                        const type = (item?.type || '').toString();
                        const isGood = ['approved', 'executed', 'scan_complete', 'hard_take_profit', 'take_profit_90c', 'trailing_take_profit', 'let_it_ride_trail_exit', 'let_it_ride_activated', 'dynamic_take_profit', 'dynamic_trailing_tp', 'high_profit_lock', 'profit_lock', 'pre_resolution_exit'].includes(type);
                        const isWarn = ['blocked', 'rejected', 'failed', 'skipped', 'no_trades_reason', 'grace_period_block', 'catastrophic_floor_exit', 'half_sell', 'emergency_exit', 'rapid_decline_exit'].includes(type);
                        const isFlip = ['hedge_flip', 'hedge_flip_triggered', 'hedge_executed'].includes(type);

                        const title =
                          type === 'approved' ? '✅ APPROVED' :
                          type === 'blocked' ? '⛔ BLOCKED' :
                          type === 'rejected' ? '🚫 REJECTED' :
                          type === 'executing' ? '⏳ EXECUTING' :
                          type === 'executed' ? '✅ EXECUTED' :
                          type === 'failed' ? '❌ FAILED' :
                          type === 'scan_start' ? '🔎 SCAN START' :
                          type === 'scan_complete' ? '✅ SCAN COMPLETE' :
                          type === 'no_trades_reason' ? 'ℹ️ NO TRADES' :
                          type === 'position_exit' ? '📤 EXIT' :
                          type === 'hard_take_profit' ? '💰 HARD TP' :
                          type === 'dynamic_take_profit' ? '🎯 DYNAMIC TP' :
                          type === 'dynamic_trailing_tp' ? '📈 TRAILING TP' :
                          type === 'take_profit_90c' ? '💎 90¢ TP' :
                          type === 'high_profit_lock' ? '🔒 PROFIT LOCK' :
                          type === 'profit_lock' ? '🔒 PROFIT LOCK' :
                          type === 'pre_resolution_exit' ? '⏰ PRE-RESOLUTION EXIT' :
                          type === 'let_it_ride_activated' ? '🏄 LET IT RIDE ACTIVATED' :
                          type === 'let_it_ride_trail_exit' ? '🏄 LET IT RIDE EXIT' :
                          type === 'grace_period_block' ? '⏳ GRACE PERIOD' :
                          type === 'hedge_flip_triggered' ? '🔄 HEDGE FLIP QUEUED' :
                          type === 'hedge_executed' ? '🔄 HEDGE EXECUTED' :
                          type === 'catastrophic_floor_exit' ? '🚨 CATASTROPHIC EXIT' :
                          type === 'half_sell' ? '✂️ HALF SELL' :
                          type === 'emergency_exit' ? '🚨 EMERGENCY EXIT' :
                          type === 'rapid_decline_exit' ? '📉 RAPID DECLINE EXIT' :
                          type ? type.toUpperCase() : 'EVENT';

                        const confidence = Number.isFinite(item?.confidence) ? item.confidence : null;
                        let market = item?.market || item?.message || '';
                        const reason = item?.reason || '';

                        if (!market && type === 'scan_start') {
                          market = `Markets=${item?.marketCount} open=${item?.openPositions}/${item?.maxOpen} dailyTrades=${item?.dailyTrades}`;
                        }
                        if (!market && type === 'scan_complete') {
                          market = `fetched=${item?.marketsFetched} eligible=${item?.eligibleMarkets} eval=${item?.evaluatedMarkets} opened=${item?.tradesOpened} closed=${item?.tradesClosed}`;
                        }
                        if (!market && type === 'no_trades_reason') {
                          market = item?.message || 'No trades this tick';
                        }

                        const skipped = item?.skipped;
                        const skippedSummary = skipped && typeof skipped === 'object'
                          ? Object.entries(skipped).filter(([, v]) => typeof v === 'number' && v > 0).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([k, v]) => `${k}:${v}`).join('  ')
                          : '';

                        const detailLines = [];
                        if (item?.amount) detailLines.push(String(item.amount));
                        if (item?.tradeAmount) detailLines.push(`$${item.tradeAmount}`);
                        if (item?.price) detailLines.push(String(item.price));
                        if (item?.outcome) detailLines.push(String(item.outcome));
                        if (skippedSummary) detailLines.push(`skipped: ${skippedSummary}`);

                        const detailsObj = item?.details;
                        if (detailsObj && typeof detailsObj === 'object') {
                          const parts = [];
                          if (typeof detailsObj.availableBalance === 'number') parts.push(`bal=$${detailsObj.availableBalance.toFixed(2)}`);
                          if (typeof detailsObj.tradableBalance === 'number') parts.push(`tradable=$${detailsObj.tradableBalance.toFixed(2)}`);
                          if (typeof detailsObj.openPositionsValue === 'number') parts.push(`open=$${detailsObj.openPositionsValue.toFixed(2)}`);
                          if (typeof detailsObj.currentPortfolioRisk === 'number') parts.push(`risk=${detailsObj.currentPortfolioRisk.toFixed(1)}%`);
                          if (typeof detailsObj.maxPortfolioRisk === 'number') parts.push(`maxPort=${detailsObj.maxPortfolioRisk.toFixed(1)}%`);
                          if (typeof detailsObj.maxSingleTradeRisk === 'number') parts.push(`maxSingle=${detailsObj.maxSingleTradeRisk.toFixed(1)}%`);
                          if (parts.length) detailLines.push(parts.join(' '));
                        }

                        const detailsText = detailLines.join(' • ');

                        return (
                          <div 
                            key={idx}
                            className={`p-2 rounded-lg text-xs ${
                              isFlip ? 'bg-cyan-500/10 border border-cyan-500/30' :
                              isGood ? 'bg-green-500/10 border border-green-500/30' :
                              isWarn ? 'bg-amber-500/10 border border-amber-500/30' :
                              'bg-slate-800/50 border border-slate-700'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className={isFlip ? 'text-cyan-400 font-bold' : isGood ? 'text-green-400 font-bold' : isWarn ? 'text-amber-300 font-bold' : 'text-slate-300 font-bold'}>
                                {title}
                              </span>
                              <div className="flex items-center gap-2">
                                {Number.isFinite(item?.pnlPercent) && (
                                  <span className={item.pnlPercent >= 0 ? 'text-green-400 font-mono' : 'text-red-400 font-mono'}>
                                    {item.pnlPercent >= 0 ? '+' : ''}{item.pnlPercent.toFixed(1)}%
                                  </span>
                                )}
                                <span className="text-dark-500">{confidence != null ? `${confidence}%` : ''}</span>
                              </div>
                            </div>
                            {market ? <p className="text-slate-300 mt-1 truncate">{market}</p> : null}
                            {reason ? <p className="text-slate-400 mt-1 truncate">{reason}</p> : null}
                            {item?.timestamp && <p className="text-dark-600 mt-0.5 text-[10px]">{new Date(item.timestamp).toLocaleTimeString()}</p>}
                            {detailsText ? <p className={isGood ? 'text-green-400 mt-1 truncate' : 'text-slate-400 mt-1 truncate'}>{detailsText}</p> : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {/* Activity Log */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary-400" />
                <h3 className="font-semibold">Activity Log</h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={syncPositions}
                  disabled={isSyncing}
                  className="px-2 py-1 text-xs rounded-lg bg-cyan-600/20 text-cyan-400 hover:bg-cyan-600/30 transition-colors border border-cyan-500/30"
                  title="Sync positions from Polymarket"
                >
                  {isSyncing ? 'Syncing...' : '🔄 Sync'}
                </button>
                <button
                  onClick={fetchActivity}
                  disabled={isActivityLoading}
                  className="p-1.5 rounded-lg hover:bg-dark-700 transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 ${isActivityLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            
            {activityStats && (
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="p-2 rounded-lg bg-dark-800/60 border border-dark-700 text-center">
                  <p className="text-xs text-dark-500">Open</p>
                  <p className="text-lg font-semibold text-emerald-400">{activityStats.openPositions || 0}</p>
                </div>
                <div className="p-2 rounded-lg bg-dark-800/60 border border-dark-700 text-center">
                  <p className="text-xs text-dark-500">Closed</p>
                  <p className="text-lg font-semibold">{activityStats.closedPositions || 0}</p>
                </div>
              </div>
            )}
            
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {activity.length === 0 ? (
                <p className="text-sm text-dark-500 text-center py-4">No activity yet</p>
              ) : (
                activity.slice(0, 10).map((item, idx) => (
                  <div key={idx} className="p-2 rounded-lg bg-dark-800/40 border border-dark-700/50">
                    <div className="flex items-center gap-2">
                      {item.type === 'position_opened' && (
                        <TrendingUp className="w-4 h-4 text-emerald-400" />
                      )}
                      {item.type === 'position_closed' && (
                        <TrendingDown className="w-4 h-4 text-amber-400" />
                      )}
                      {item.type === 'trade' && (
                        <Activity className="w-4 h-4 text-primary-400" />
                      )}
                      <span className="text-xs font-medium">
                        {item.type === 'position_opened' ? 'Opened' : 
                         item.type === 'position_closed' ? 'Closed' : 'Trade'}
                      </span>
                      <span className="text-xs text-dark-500 ml-auto flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(item.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="text-xs text-dark-400 mt-1 truncate">{item.market}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs">
                      {item.side && (
                        <span className={item.side === 'BUY' ? 'text-emerald-400' : 'text-red-400'}>
                          {item.side}
                        </span>
                      )}
                      {item.size && <span>{item.size?.toFixed(2)} shares</span>}
                      {item.price && <span>@ ${item.price?.toFixed(2)}</span>}
                      {item.pnl !== undefined && item.pnl !== null && (
                        <span className={item.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                          {item.pnl >= 0 ? '+' : ''}{item.pnl?.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {activity.length > 0 && (
              <div className="mt-3 pt-3 border-t border-dark-700">
                <Link 
                  to="/positions?status=all" 
                  className="text-xs text-primary-400 hover:text-primary-300 transition-colors"
                >
                  View all positions →
                </Link>
              </div>
            )}
          </div>

          <div className="card">
            <h3 className="font-semibold">Locked Area</h3>
            <p className="text-sm text-dark-400 mt-2">
              Whale Vault is designed as a locked container where additional high-end modes and tooling will be shipped.
            </p>
            <div className="mt-4 space-y-2 text-sm text-dark-400">
              <p>- Autonomous strategies (no copy trading)</p>
              <p>- Risk gating and safety rails</p>
              <p>- Signal explainability</p>
            </div>
          </div>

          <div className="card">
            <h3 className="font-semibold">Trading Mode</h3>
            <p className="text-sm text-dark-400 mt-2">
              Whale Mode supports both Paper and Real trading. Paper mode simulates trades for testing, 
              while Real mode executes actual trades on Polymarket.
            </p>
            <div className="mt-4">
              <Link to="/paper" className="text-sm text-primary-400 hover:text-primary-300 transition-colors">
                View paper positions
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
